import { useCallback, useEffect, useRef, useState } from "react";
import {
  Compass,
  House,
  Menu,
  MoreVertical,
  Plug,
  Plus,
  ScanHeart,
  Sparkles,
  Users,
  UserPlus,
  Shield,
  X,
} from "lucide-react";
import type {
  Goal,
  IntegrationStatus,
  OrgEvent,
  Product,
  Quest,
} from "@outpost/shared";
import {
  CommunityPane,
  COMMUNITY_INVITE_TEXT,
  COMMUNITY_INVITE_XP,
  COMMUNITY_SUBNAV,
  type CommunityDest,
} from "./CommunityPane";
import { AgentsPane } from "./AgentsPane";
import { ConnectionsPanel, cleanSecret, parseBufferStats, bufferStatsForProductScope, bufferQueueBand, bufferQueueBandLabel, parseInstantlyStats, instantlyGrowthForProductScope, parseGaMeta, parseGscMeta, googleGrowthForProductScope, gscGrowthForProductScope, parseAhrefsMeta, ahrefsGrowthForProductScope, parsePostHogMeta, posthogGrowthForProductScope, type BufferMetricWindow } from "./ConnectionsPanel";
import {
  GrowthSignalChapter,
  GrowthTicker,
  formatPctDelta,
  formatSignedDelta,
  pctFromPrior,
  seriesSpanDelta,
  type GrowthFreshness,
  type GrowthMetricTile,
  type GrowthTickerItem,
} from "./GrowthSignalChapter";
import { OpsPane } from "./OpsPane";
import type { PropertyRow, SiteRow } from "./GoogleConnectWizard";
import {
  CATALOG_VERSION,
  SEED_QUEST_CATALOG,
  XP_PER_QUEST,
  decadeStart,
  isMinLevelLocked,
  lookoutLevelFromXp,
  matchSeedInQuests,
  seedDescription,
  apiTierFromMinLevel,
  findSeedForQuest,
  opsGapToSeedId,
  type QuestChannel,
  type SeedQuest,
} from "./questCatalog";
import { QuestsPane } from "./QuestsPane";
import { AdminPane } from "./AdminPane";

const NAV = [
  { id: "home", label: "Home", Icon: House },
  { id: "monitoring", label: "Monitoring", Icon: ScanHeart },
  { id: "quests", label: "Quests", Icon: Compass },
  { id: "community", label: "Community", Icon: Users },
] as const;

type NavId = (typeof NAV)[number]["id"] | "agents" | "admin" | "product_settings";

type DigestNextAction = {
  action: string;
  productName: string | null;
  productId?: string | null;
  reason: string;
  targetType?: "quest" | "goal" | "connection" | "product";
  targetId?: string;
};

type MetricWindow = "day" | "week" | "month";

type MonitoringTab = "growth" | "ops" | "connections" | "sessions";

const MONITORING_SUBNAV: { id: MonitoringTab; label: string }[] = [
  { id: "growth", label: "Growth" },
  { id: "ops", label: "Operations" },
  { id: "connections", label: "Connections" },
  { id: "sessions", label: "Sessions" },
];

type CustomerCircleResponse = {
  productId: string | null;
  window: MetricWindow;
  status:
    | "unavailable"
    | "disconnected"
    | "ok"
    | "stale"
    | "zero"
    | "connected"
    | "unmapped";
  message?: string;
  totals: {
    newSignups: number | null;
    firstValue: number | null;
    freemiumActive: number | null;
    paying: number | null;
  };
  comparison?: {
    priorNewSignups: number | null;
    deltaNewSignups: number | null;
    pctNewSignups: number | null;
    priorFirstValue: number | null;
    deltaFirstValue: number | null;
    pctFirstValue: number | null;
    label: "vs prior 7d" | "vs prior 30d";
  } | null;
  markers: unknown[];
  asOf: string | null;
};

type IntegrationCard = {
  id: string | null;
  organizationId: string;
  productId: string | null;
  provider: string;
  label: string;
  status: IntegrationStatus;
  lastSyncedAt: string | null;
  metaJson: string | null;
  createdAt: string | null;
  live?: boolean;
  credentialsConfigured?: boolean;
};

const WINDOWS: { id: MetricWindow; label: string }[] = [
  { id: "day", label: "Day" },
  { id: "week", label: "Week" },
  { id: "month", label: "Month" },
];

const TOKEN_KEY = "outpost16_session_token";
const LEGACY_TOKEN_KEY = "outpost16_founder_token";

type MeResponse = {
  kind: "founder" | "user" | "mcp";
  user?: {
    id: string;
    email: string;
    name: string | null;
    isPlatformAdmin: boolean;
  };
  role?: string | null;
  activeOrganizationId?: string;
  memberships?: Array<{
    organizationId: string;
    role: string;
    orgName: string;
    planTier: string;
  }>;
};

function consolePath(): string {
  return window.location.pathname.replace(/\/+$/, "") || "/";
}

function isJoinPath(): boolean {
  const p = consolePath();
  return p === "/join" || p.startsWith("/join");
}

function inviteTokenFromUrl(): string {
  try {
    return new URLSearchParams(window.location.search).get("invite")?.trim() || "";
  } catch {
    return "";
  }
}

function questTypeGlyphSrc(quest: Quest): string {
  const t = `${quest.title} ${quest.description ?? ""}`.toLowerCase();
  if (/landing|use-case|industry|use case/.test(t)) {
    return "/brand/outpost16-quest-type-landing.png";
  }
  if (/\bvs\b|versus|competitor|compare/.test(t)) {
    return "/brand/outpost16-quest-type-compare.png";
  }
  if (/\btool\b|mini-tool|calculator|checklist/.test(t)) {
    return "/brand/outpost16-quest-type-tool.png";
  }
  return "/brand/outpost16-quest-assign-flag.png";
}

function getStoredToken(): string {
  try {
    const current = sessionStorage.getItem(TOKEN_KEY);
    if (current) return current;
    const legacy = sessionStorage.getItem(LEGACY_TOKEN_KEY);
    if (legacy) {
      sessionStorage.setItem(TOKEN_KEY, legacy);
      sessionStorage.removeItem(LEGACY_TOKEN_KEY);
      return legacy;
    }
    return "";
  } catch {
    return "";
  }
}

function orgEventKindLabel(kind: string): string {
  switch (kind) {
    case "follow.received":
      return "Follow";
    case "agent.progress":
      return "Agent";
    case "quest.completed":
      return "Quest";
    case "connector.connected":
      return "Connector";
    case "stripe.customer":
      return "Stripe";
    case "stripe.payment":
      return "Payment";
    default:
      return kind;
  }
}

function relativeTimeLabel(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "just now";
  const sec = Math.floor(ms / 1000);
  if (sec < 45) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

type EventToast = {
  id: string;
  title: string;
  body: string | null;
  severity: string;
  kind: string;
};

async function apiGet<T>(
  path: string,
  token: string,
): Promise<{ ok: true; data: T } | { ok: false; status: number; error: string }> {
  const headers: Record<string, string> = {};
  if (token && token !== "cookie-session") {
    headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(path, {
    headers,
    credentials: "include",
  });
  if (!res.ok) {
    const body = await res.text();
    return { ok: false, status: res.status, error: body.slice(0, 200) };
  }
  return { ok: true, data: (await res.json()) as T };
}

async function apiPost<T>(
  path: string,
  token: string,
  body?: unknown,
): Promise<{ ok: true; data: T } | { ok: false; status: number; error: string }> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token && token !== "cookie-session") {
    headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(path, {
    method: "POST",
    headers,
    credentials: "include",
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const text = await res.text();
    return { ok: false, status: res.status, error: text.slice(0, 800) };
  }
  return { ok: true, data: (await res.json()) as T };
}

async function apiPutBlob<T>(
  path: string,
  token: string,
  body: Blob,
  contentType: string,
): Promise<{ ok: true; data: T } | { ok: false; status: number; error: string }> {
  const headers: Record<string, string> = {
    "Content-Type": contentType,
  };
  if (token && token !== "cookie-session") {
    headers.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(path, {
    method: "PUT",
    headers,
    credentials: "include",
    body,
  });
  if (!res.ok) {
    const text = await res.text();
    return { ok: false, status: res.status, error: text.slice(0, 200) };
  }
  return { ok: true, data: (await res.json()) as T };
}

export function App() {
  const [active, setActive] = useState<NavId>("home");
  const [communityFocus, setCommunityFocus] = useState<CommunityDest>("feed");
  const [communityFocusNonce, setCommunityFocusNonce] = useState(0);
  const [newStartupNonce, setNewStartupNonce] = useState(0);
  const [inviteNotice, setInviteNotice] = useState("");
  const [inviteXpClaimed, setInviteXpClaimed] = useState(false);
  const [inviteBusy, setInviteBusy] = useState(false);
  const [token, setToken] = useState(getStoredToken);
  const [tokenDraft, setTokenDraft] = useState("");
  const [emailDraft, setEmailDraft] = useState("");
  const [magicSent, setMagicSent] = useState(false);
  const [magicVerifyUrl, setMagicVerifyUrl] = useState<string | null>(null);
  const [showAdvancedToken, setShowAdvancedToken] = useState(false);
  const [me, setMe] = useState<MeResponse | null>(null);
  const [joinInvite] = useState(() => inviteTokenFromUrl());
  const [joinPreview, setJoinPreview] = useState<{
    email: string;
    orgName: string;
    role: string;
    accepted: boolean;
    revoked: boolean;
    expired: boolean;
  } | null>(null);
  const [joinPreviewError, setJoinPreviewError] = useState("");
  const [joinMode] = useState(() => isJoinPath());
  const [products, setProducts] = useState<Product[]>([]);
  const [orgEvents, setOrgEvents] = useState<OrgEvent[]>([]);
  const [eventToasts, setEventToasts] = useState<EventToast[]>([]);
  const [struggleDigest, setStruggleDigest] = useState<{
    headline: string;
    summary?: string;
    topProductName: string | null;
    nextAgent: DigestNextAction | null;
    nextFounder: DigestNextAction[];
    signals: Array<{
      kind: string;
      productName: string | null;
      summary: string;
      fixture?: boolean;
    }>;
    fixtureSignals?: Array<{ summary: string }>;
  } | null>(null);
  const [digestWhyOpen, setDigestWhyOpen] = useState(false);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [quests, setQuests] = useState<Quest[]>([]);
  const [ledgerXp, setLedgerXp] = useState<number | null>(null);
  const [status, setStatus] = useState<string>("");
  const [questNotice, setQuestNotice] = useState<string>("");
  const [channelFilter, setChannelFilter] = useState<QuestChannel | "ALL">(
    "ALL",
  );
  const [assigneeFilter, setAssigneeFilter] = useState<
    "ALL" | "human" | "agent" | "either"
  >("ALL");
  const [missionFilter, setMissionFilter] = useState<string | "ALL">("ALL");
  const [expandedDecade, setExpandedDecade] = useState<number | null>(null);
  const [myActiveOpen, setMyActiveOpen] = useState(true);
  const [expandedCatalogId, setExpandedCatalogId] = useState<string | null>(
    null,
  );
  const [expandedActiveId, setExpandedActiveId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [proofDrafts, setProofDrafts] = useState<Record<string, string>>({});
  const [busyQuestId, setBusyQuestId] = useState<string | null>(null);
  const [monitoringTab, setMonitoringTab] = useState<MonitoringTab>("growth");
  const [metricWindow, setMetricWindow] = useState<MetricWindow>("month");
  const [metricProductId, setMetricProductId] = useState<string>("");
  const [circle, setCircle] = useState<CustomerCircleResponse | null>(null);
  const [circleError, setCircleError] = useState<string>("");
  const [circleLoading, setCircleLoading] = useState(false);
  const [integrations, setIntegrations] = useState<IntegrationCard[]>([]);
  const [integrationsError, setIntegrationsError] = useState<string>("");
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  const [integrationBusy, setIntegrationBusy] = useState<string>("");
  const [integrationNotice, setIntegrationNotice] = useState("");
  const [integrationActionError, setIntegrationActionError] = useState("");
  const [secretsKeyConfigured, setSecretsKeyConfigured] = useState(true);
  const [ahrefsPlatformKeyConfigured, setAhrefsPlatformKeyConfigured] =
    useState(false);
  /** Bumps after background sync so Growth/Connections re-read connector snapshots. */
  const [integrationsEpoch, setIntegrationsEpoch] = useState(0);
  const bgSyncInFlight = useRef(false);
  const [bufferApiKey, setBufferApiKey] = useState("");
  const [instantlyApiKey, setInstantlyApiKey] = useState("");
  const [ahrefsApiKey, setAhrefsApiKey] = useState("");
  const [googleClientId, setGoogleClientId] = useState("");
  const [googleClientSecret, setGoogleClientSecret] = useState("");
  const [connPickerOpen, setConnPickerOpen] = useState(false);
  const [draftProvider, setDraftProvider] = useState<string | null>(null);
  const [tuneProvider, setTuneProvider] = useState<string | null>(null);
  const [logoPreviews, setLogoPreviews] = useState<Record<string, string>>({});
  const [logoBusyId, setLogoBusyId] = useState<string | null>(null);
  const [logoNotice, setLogoNotice] = useState<string>("");
  const [productFilter, setProductFilter] = useState<string>("all");
  const [productMenuId, setProductMenuId] = useState<string | null>(null);
  const [settingsProductId, setSettingsProductId] = useState<string | null>(
    null,
  );
  const [promptDraft, setPromptDraft] = useState("");
  const [homeNotice, setHomeNotice] = useState("");
  const [rewardBurstKey, setRewardBurstKey] = useState(0);
  const [showRewardBurst, setShowRewardBurst] = useState(false);
  /** locked = login wall; checking = verifying session; open = workspace */
  const [gate, setGate] = useState<"locked" | "checking" | "open">(() => {
    if (isJoinPath()) return "locked";
    if (getStoredToken()) return "checking";
    try {
      if (new URLSearchParams(window.location.search).get("signed_in") === "1") {
        return "checking";
      }
    } catch {
      /* ignore */
    }
    // Probe HttpOnly cookie session on first paint
    return "checking";
  });
  const [gateError, setGateError] = useState("");
  const [gateBusy, setGateBusy] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [selfDisplayName, setSelfDisplayName] = useState("Robin");
  const [selfAvatarKey, setSelfAvatarKey] = useState<string | null>(null);
  const [selfAvatarUrl, setSelfAvatarUrl] = useState<string | null>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const questCatalogRef = useRef<HTMLDivElement>(null);

  const refreshGoalsQuests = useCallback(async (t: string) => {
    const [g, q, prog] = await Promise.all([
      apiGet<{ goals: Goal[] }>("/v1/goals", t),
      apiGet<{ quests: Quest[] }>("/v1/quests", t),
      apiGet<{ xp: number }>("/v1/progression", t),
    ]);
    if (g.ok) setGoals(g.data.goals);
    if (q.ok) setQuests(q.data.quests);
    if (prog.ok && typeof prog.data.xp === "number") {
      setLedgerXp(prog.data.xp);
    }
    return { goalsOk: g.ok, questsOk: q.ok };
  }, []);

  useEffect(() => {
    if (joinMode) {
      setGate("locked");
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setStatus("");
      setGate("checking");
      const authHeaders: Record<string, string> = {};
      if (token && token !== "cookie-session") {
        authHeaders.Authorization = `Bearer ${token}`;
      }

      const meRes = await fetch("/v1/me", {
        headers: authHeaders,
        credentials: "include",
      });
      if (!meRes.ok) {
        if (!cancelled) {
          try {
            sessionStorage.removeItem(TOKEN_KEY);
          } catch {
            /* ignore */
          }
          setToken("");
          setMe(null);
          setProducts([]);
          setOrgEvents([]);
          setEventToasts([]);
          setGoals([]);
          setQuests([]);
          setLedgerXp(null);
          setGate("locked");
          if (token) {
            setGateError(
              meRes.status === 401
                ? "Session expired — sign in again."
                : `Auth failed (${meRes.status}).`,
            );
          } else {
            setGateError("");
          }
          setLoading(false);
        }
        return;
      }

      const meData = (await meRes.json()) as MeResponse;
      if (cancelled) return;
      setMe(meData);
      if (meData.user?.name) {
        setSelfDisplayName(meData.user.name.split(" ")[0] || meData.user.name);
      }
      // Cookie sessions: keep a sentinel so existing `if (!token)` guards still work
      if (!token) {
        try {
          sessionStorage.setItem(TOKEN_KEY, "cookie-session");
        } catch {
          /* ignore */
        }
        setToken("cookie-session");
        return; // re-run effect with sentinel
      }

      const boot = await fetch("/v1/bootstrap", {
        method: "POST",
        headers:
          token === "cookie-session"
            ? {}
            : authHeaders,
        credentials: "include",
      });
      if (!boot.ok) {
        if (!cancelled) {
          setGate("locked");
          setGateError(`Bootstrap failed (${boot.status}).`);
          setLoading(false);
        }
        return;
      }

      const [prod, eventsRes, digest] = await Promise.all([
        apiGet<{ products: Product[] }>("/v1/products", token),
        apiGet<{ events: OrgEvent[] }>("/v1/events", token),
        apiGet<{
          headline: string;
          summary?: string;
          topProductName: string | null;
          nextAgent: DigestNextAction | null;
          nextFounder: DigestNextAction[];
          signals: Array<{
            kind: string;
            productName: string | null;
            summary: string;
            fixture?: boolean;
          }>;
          fixtureSignals?: Array<{ summary: string }>;
        }>("/v1/struggle-digest", token),
      ]);
      const gq = await refreshGoalsQuests(token);
      if (cancelled) return;
      if (prod.ok) setProducts(prod.data.products);
      if (eventsRes.ok) setOrgEvents(eventsRes.data.events);
      if (digest.ok) setStruggleDigest(digest.data);
      if (!prod.ok || !gq.goalsOk || !gq.questsOk) {
        setStatus("Partial load — some endpoints failed.");
      }
      try {
        const u = new URL(window.location.href);
        if (u.searchParams.get("signed_in")) {
          u.searchParams.delete("signed_in");
          window.history.replaceState({}, "", u.pathname + u.search);
        }
      } catch {
        /* ignore */
      }
      setGate("open");
      setGateError("");
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [token, refreshGoalsQuests, joinMode]);


  useEffect(() => {
    if (gate !== "open" || !token) return;
    const ac = new AbortController();
    void (async () => {
      try {
        const res = await fetch("/v1/agent-activity/stream", {
          headers: { Authorization: `Bearer ${token}` },
          signal: ac.signal,
        });
        if (!res.ok || !res.body) return;
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const parts = buf.split("\n\n");
          buf = parts.pop() || "";
          for (const part of parts) {
            if (!part.includes("event: org_event")) continue;
            const line = part.split("\n").find((l) => l.startsWith("data: "));
            if (!line) continue;
            try {
              const payload = JSON.parse(line.slice(6)) as OrgEvent & {
                toast?: boolean;
              };
              if (!payload?.id) continue;
              setOrgEvents((prev) => {
                if (prev.some((e) => e.id === payload.id)) return prev;
                const next: OrgEvent = {
                  id: payload.id,
                  organizationId: payload.organizationId,
                  kind: payload.kind,
                  title: payload.title,
                  body: payload.body ?? null,
                  severity: payload.severity,
                  source: payload.source ?? null,
                  productId: payload.productId ?? null,
                  actorId: payload.actorId ?? null,
                  refType: payload.refType ?? null,
                  refId: payload.refId ?? null,
                  sharedToFeed: Boolean(payload.sharedToFeed),
                  readAt: payload.readAt ?? null,
                  dismissedAt: payload.dismissedAt ?? null,
                  createdAt: payload.createdAt,
                };
                return [next, ...prev].slice(0, 60);
              });
              if (payload.toast === false) continue;
              setEventToasts((prev) => {
                if (prev.some((t) => t.id === payload.id)) return prev;
                return [
                  {
                    id: payload.id,
                    title: payload.title,
                    body: payload.body ?? null,
                    severity: payload.severity || "info",
                    kind: String(payload.kind),
                  },
                  ...prev,
                ].slice(0, 5);
              });
              if ((payload.severity || "info") === "info") {
                window.setTimeout(() => {
                  setEventToasts((prev) =>
                    prev.filter((t) => t.id !== payload.id),
                  );
                }, 4000);
              }
            } catch {
              /* ignore malformed */
            }
          }
        }
      } catch {
        /* aborted or offline */
      }
    })();
    return () => ac.abort();
  }, [gate, token]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const go = params.get("google_oauth");
    if (!go) return;
    setActive("monitoring");
    setMonitoringTab("connections");
    if (go === "ok") {
      const provider = params.get("provider") || "ga";
      setIntegrationNotice(
        `${provider} connected via Google OAuth — lookout lit.`,
      );
      setIntegrationActionError("");
      setDraftProvider(null);
      setConnPickerOpen(false);
      setRewardBurstKey((k) => k + 1);
    } else {
      setIntegrationActionError(
        params.get("message") || "Google OAuth failed",
      );
    }
    window.history.replaceState({}, "", "/app");
  }, []);

  useEffect(() => {
    if (!token) {
      setIntegrations([]);
      setIntegrationsError("");
      setIntegrationsLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setIntegrationsLoading(true);
      setIntegrationsError("");
      const intRes = await apiGet<{
        integrations: IntegrationCard[];
        note?: string;
        secretsKeyConfigured?: boolean;
        ahrefsPlatformKeyConfigured?: boolean;
      }>("/v1/integrations", token);
      if (cancelled) return;
      if (!intRes.ok) {
        setIntegrations([]);
        setIntegrationsError(`Integrations: ${intRes.status}`);
      } else {
        setIntegrations(intRes.data.integrations);
        setSecretsKeyConfigured(intRes.data.secretsKeyConfigured !== false);
        setAhrefsPlatformKeyConfigured(
          intRes.data.ahrefsPlatformKeyConfigured === true,
        );
      }
      setIntegrationsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [token, integrationNotice, integrationActionError, integrationsEpoch]);

  // Keep the lookout warm: sync on login, when the tab becomes visible, and every 15m.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const INTERVAL_MS = 15 * 60 * 1000;
    const MIN_AGE_MINUTES = 10;

    async function backgroundSync() {
      if (cancelled || bgSyncInFlight.current) return;
      if (document.visibilityState === "hidden") return;
      bgSyncInFlight.current = true;
      try {
        const res = await apiPost<{
          ok?: boolean;
          synced?: number;
        }>("/v1/integrations/sync-all", token, {
          minAgeMinutes: MIN_AGE_MINUTES,
        });
        if (!cancelled && res.ok) {
          setIntegrationsEpoch((n) => n + 1);
        }
      } finally {
        bgSyncInFlight.current = false;
      }
    }

    void backgroundSync();
    const id = window.setInterval(() => void backgroundSync(), INTERVAL_MS);
    const onVis = () => {
      if (document.visibilityState === "visible") void backgroundSync();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [token]);

  async function startGoogleOAuth(payload: {
    properties: PropertyRow[];
    sites: SiteRow[];
  }) {
    if (!token) return;
    setIntegrationBusy("ga:oauth");
    setIntegrationNotice("");
    setIntegrationActionError("");
    const res = await apiPost<{
      ok?: boolean;
      authorizeUrl?: string;
      redirectUri?: string;
      hint?: string;
      message?: string;
      error?: string;
    }>("/v1/integrations/google/oauth/start", token, {
      clientId: cleanSecret(googleClientId) || undefined,
      clientSecret: cleanSecret(googleClientSecret) || undefined,
      provider: "ga",
      properties: payload.properties,
      sites: payload.sites,
      propertyId: payload.properties[0]?.propertyId,
      siteUrl: payload.sites[0]?.siteUrl,
    });
    if (!res.ok) {
      let msg = res.error;
      try {
        const parsed = JSON.parse(res.error) as { message?: string };
        if (parsed.message) msg = parsed.message;
      } catch {
        /* keep */
      }
      setIntegrationActionError(
        `Google OAuth start failed: ${msg}. Confirm the Web client has redirect URI https://outpost16.com/v1/integrations/google/oauth/callback`,
      );
      setIntegrationBusy("");
      return;
    }
    if (!res.data.authorizeUrl) {
      setIntegrationActionError("Google OAuth start returned no authorize URL");
      setIntegrationBusy("");
      return;
    }
    window.location.assign(res.data.authorizeUrl);
  }

  async function saveGoogleMappings(
    payload: {
      properties: PropertyRow[];
      sites: SiteRow[];
    },
    surface: "ga" | "gsc" = "ga",
  ) {
    if (!token) return;
    setIntegrationBusy(`${surface}:connect`);
    setIntegrationNotice("");
    setIntegrationActionError("");
    const body: Record<string, unknown> = {
      properties: payload.properties,
      sites: payload.sites,
    };
    if (payload.properties[0]?.propertyId) {
      body.propertyId = payload.properties[0].propertyId;
    }
    if (payload.sites[0]?.siteUrl) {
      body.siteUrl = payload.sites[0].siteUrl;
    }
    if (googleClientId.trim()) body.clientId = cleanSecret(googleClientId);
    if (googleClientSecret.trim()) {
      body.clientSecret = cleanSecret(googleClientSecret);
    }
    // GSC card must hit /gsc even when GA properties are preserved in the payload.
    const provider =
      surface === "gsc"
        ? "gsc"
        : payload.properties.length > 0
          ? "ga"
          : payload.sites.length > 0
            ? "gsc"
            : "ga";
    const res = await apiPost<{
      ok?: boolean;
      message?: string;
      error?: string;
    }>(`/v1/integrations/${provider}/connect`, token, body);
    if (!res.ok) {
      let msg = res.error;
      try {
        const parsed = JSON.parse(res.error) as {
          message?: string;
          error?: string;
        };
        if (parsed.message) msg = parsed.message;
        else if (parsed.error) msg = parsed.error;
      } catch {
        /* keep */
      }
      setIntegrationActionError(`Save mappings failed: ${msg}`);
      setIntegrationBusy("");
      return;
    }
    setIntegrationNotice(
      provider === "ga"
        ? "Google Analytics mappings saved. Search Console sites updated when provided."
        : "Search Console mappings saved.",
    );
    setIntegrationBusy("");
    setConnPickerOpen(false);
    setDraftProvider(null);
  }

  async function saveAhrefs(payload: {
    targets: { target: string; productId: string }[];
  }) {
    if (!token) return;
    setIntegrationBusy("ahrefs:connect");
    setIntegrationNotice("");
    setIntegrationActionError("");
    const body: Record<string, unknown> = {
      targets: payload.targets,
    };
    if (ahrefsApiKey.trim()) body.apiKey = ahrefsApiKey.trim();
    const res = await apiPost<{
      ok?: boolean;
      message?: string;
      error?: string;
      quest?: {
        seedId: string;
        questId: string;
        completed: boolean;
        xpAwarded: boolean;
        alreadyComplete: boolean;
      } | null;
    }>("/v1/integrations/ahrefs/connect", token, body);
    if (!res.ok) {
      let msg = res.error;
      try {
        const parsed = JSON.parse(res.error) as {
          message?: string;
          error?: string;
        };
        if (parsed.message) msg = parsed.message;
        else if (parsed.error) msg = parsed.error;
      } catch {
        /* keep */
      }
      setIntegrationActionError(`Ahrefs connect failed: ${msg}`);
      setIntegrationBusy("");
      return;
    }
    setAhrefsApiKey("");
    const q = res.data.quest;
    let questBit = "";
    if (q?.completed) {
      questBit = q.xpAwarded
        ? ` · Quest ${q.seedId} complete (+40 XP)`
        : q.alreadyComplete
          ? ` · Quest ${q.seedId} already complete`
          : ` · Quest ${q.seedId} complete`;
      setRewardBurstKey((k) => k + 1);
      await refreshGoalsQuests(token);
    }
    setIntegrationNotice(
      `Ahrefs lit — Domain Rating synced.${questBit}`,
    );
    setIntegrationBusy("");
    setConnPickerOpen(false);
    setDraftProvider(null);
  }

  async function savePostHog(payload: {
    projects: {
      productId: string;
      host: string;
      projectId: string;
      apiKey?: string;
      signupEvent: string;
      firstValueEvent: string;
    }[];
  }) {
    if (!token) return;
    setIntegrationBusy("posthog:connect");
    setIntegrationNotice("");
    setIntegrationActionError("");
    const res = await apiPost<{
      ok?: boolean;
      message?: string;
      error?: string;
      quest?: {
        seedId: string;
        questId: string;
        completed: boolean;
        xpAwarded: boolean;
        alreadyComplete: boolean;
      } | null;
    }>("/v1/integrations/posthog/connect", token, {
      projects: payload.projects,
    });
    if (!res.ok) {
      let msg = res.error;
      try {
        const parsed = JSON.parse(res.error) as {
          message?: string;
          error?: string;
        };
        if (parsed.message) msg = parsed.message;
        else if (parsed.error) msg = parsed.error;
      } catch {
        /* keep */
      }
      setIntegrationActionError(`PostHog save failed: ${msg}`);
      setIntegrationBusy("");
      return;
    }
    const q = res.data.quest;
    let questBit = "";
    if (q?.completed) {
      questBit = q.xpAwarded
        ? ` · Quest ${q.seedId} complete (+40 XP)`
        : q.alreadyComplete
          ? ` · Quest ${q.seedId} already complete`
          : ` · Quest ${q.seedId} complete`;
      setRewardBurstKey((k) => k + 1);
      await refreshGoalsQuests(token);
    }
    setIntegrationNotice(`PostHog lit — activation synced.${questBit}`);
    setIntegrationBusy("");
    setConnPickerOpen(false);
    setDraftProvider(null);
  }

  async function runIntegrationAction(
    provider: string,
    action: "connect" | "sync" | "disconnect",
    extraBody?: Record<string, unknown>,
  ) {
    if (!token) return;
    setIntegrationBusy(`${provider}:${action}`);
    setIntegrationNotice("");
    setIntegrationActionError("");
    let body: Record<string, unknown> | undefined =
      extraBody && Object.keys(extraBody).length > 0
        ? { ...extraBody }
        : undefined;
    if (action !== "disconnect") {
      body = body ?? {};
      if (provider === "buffer" && bufferApiKey.trim()) {
        body.apiKey = bufferApiKey.trim();
      }
      if (provider === "instantly" && instantlyApiKey.trim()) {
        body.apiKey = instantlyApiKey.trim();
      }
      if (provider === "ahrefs" && ahrefsApiKey.trim()) {
        body.apiKey = ahrefsApiKey.trim();
      }
      if (Object.keys(body).length === 0) body = undefined;
    }
    const res = await apiPost<{
      ok?: boolean;
      message?: string;
      error?: string;
      quest?: {
        seedId: string;
        questId: string;
        completed: boolean;
        xpAwarded: boolean;
        alreadyComplete: boolean;
      } | null;
    }>(`/v1/integrations/${provider}/${action}`, token, body);
    if (!res.ok) {
      let msg = res.error;
      try {
        const parsed = JSON.parse(res.error) as {
          message?: string;
          error?: string;
        };
        if (parsed.message) msg = parsed.message;
        else if (parsed.error) msg = parsed.error;
      } catch {
        /* keep raw */
      }
      setIntegrationActionError(
        `${provider} ${action} failed: ${msg}`,
      );
    } else {
      if (action === "connect" || action === "sync") {
        setBufferApiKey("");
        setInstantlyApiKey("");
        setAhrefsApiKey("");
        setGoogleClientSecret("");
        setGoogleClientId("");
      }
      const q = res.data.quest;
      let questBit = "";
      if (q?.completed) {
        questBit = q.xpAwarded
          ? ` · Quest ${q.seedId} complete (+40 XP)`
          : q.alreadyComplete
            ? ` · Quest ${q.seedId} already complete`
            : ` · Quest ${q.seedId} complete`;
        setRewardBurstKey((k) => k + 1);
        await refreshGoalsQuests(token);
      }
      if (action === "connect") {
        setDraftProvider(null);
        setConnPickerOpen(false);
        setTuneProvider(null);
        setIntegrationNotice(
          `${provider} connected — another signal on the lookout.${questBit}`,
        );
      } else if (action === "disconnect") {
        setTuneProvider(null);
        setIntegrationNotice(`${provider} disconnected.`);
      } else {
        setIntegrationNotice(`${provider} synced.${questBit}`);
      }
    }
    setIntegrationBusy("");
  }

  useEffect(() => {
    if (!token || active !== "monitoring" || monitoringTab !== "growth") {
      return;
    }
    let cancelled = false;
    (async () => {
      setCircleLoading(true);
      setCircleError("");
      const qs = new URLSearchParams({ window: metricWindow });
      if (productFilter !== "all") qs.set("productId", productFilter);
      const circleRes = await apiGet<CustomerCircleResponse>(
        `/v1/metrics/customer-circle?${qs}`,
        token,
      );
      if (cancelled) return;
      if (!circleRes.ok) {
        setCircle(null);
        setCircleError(`Customer circle: ${circleRes.status}`);
      } else {
        setCircle(circleRes.data);
      }
      setCircleLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [token, active, monitoringTab, metricWindow, productFilter, integrationsEpoch]);

  useEffect(() => {
    if (products.length === 0) return;
    if (!metricProductId || !products.some((p) => p.id === metricProductId)) {
      setMetricProductId(products[0]?.id ?? "");
    }
  }, [products, metricProductId]);

  async function requestMagicLink() {
    const email = emailDraft.trim();
    if (!email) {
      setGateError("Enter your email.");
      return;
    }
    setGateBusy(true);
    setGateError("");
    setMagicSent(false);
    setMagicVerifyUrl(null);
    const body: { email: string; invite?: string } = { email };
    if (joinInvite) body.invite = joinInvite;
    const res = await fetch("/v1/auth/magic-link", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(body),
    });
    const data = (await res.json().catch(() => ({}))) as {
      message?: string;
      verifyUrl?: string | null;
      error?: string;
    };
    setGateBusy(false);
    if (!res.ok) {
      setGateError(data.message || data.error || `Could not send link (${res.status}).`);
      return;
    }
    setMagicSent(true);
    if (data.verifyUrl) setMagicVerifyUrl(data.verifyUrl);
  }

  async function saveToken() {
    const t = tokenDraft.trim();
    if (!t) {
      setGateError("Paste your API token.");
      return;
    }
    setGateBusy(true);
    setGateError("");
    const meRes = await fetch("/v1/me", {
      headers: { Authorization: `Bearer ${t}` },
      credentials: "include",
    });
    setGateBusy(false);
    if (!meRes.ok) {
      setGateError(
        meRes.status === 401
          ? "Invalid token."
          : `Could not connect (${meRes.status}).`,
      );
      return;
    }
    try {
      sessionStorage.setItem(TOKEN_KEY, t);
    } catch {
      /* ignore */
    }
    setMagicSent(false);
    setGate("checking");
    setToken(t);
  }

  async function clearToken() {
    try {
      await fetch("/v1/auth/logout", {
        method: "POST",
        credentials: "include",
        headers:
          token && token !== "cookie-session"
            ? { Authorization: `Bearer ${token}` }
            : {},
      });
    } catch {
      /* ignore */
    }
    try {
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(LEGACY_TOKEN_KEY);
    } catch {
      /* ignore */
    }
    setToken("");
    setTokenDraft("");
    setEmailDraft("");
    setMe(null);
    setMagicSent(false);
    setMagicVerifyUrl(null);
    setGate("locked");
    setGateError("");
    setActive("home");
    setMobileNavOpen(false);
    setProfileMenuOpen(false);
    setSelfAvatarKey(null);
    setSelfDisplayName("Robin");
  }

  useEffect(() => {
    if (!joinMode || !joinInvite) return;
    let cancelled = false;
    void (async () => {
      setJoinPreviewError("");
      const res = await fetch(
        `/v1/auth/invite-preview?invite=${encodeURIComponent(joinInvite)}`,
      );
      if (cancelled) return;
      if (!res.ok) {
        setJoinPreview(null);
        setJoinPreviewError(
          res.status === 404
            ? "Invite not found or already used."
            : `Could not load invite (${res.status}).`,
        );
        return;
      }
      const data = (await res.json()) as {
        email: string;
        orgName: string;
        role: string;
        accepted: boolean;
        revoked: boolean;
        expired: boolean;
      };
      setJoinPreview(data);
      if (data.email && data.email !== "*") setEmailDraft(data.email);
    })();
    return () => {
      cancelled = true;
    };
  }, [joinMode, joinInvite]);

  useEffect(() => {
    if (!token || gate !== "open") {
      setSelfAvatarKey(null);
      setSelfDisplayName("Robin");
      return;
    }
    let cancelled = false;
    void (async () => {
      const dir = await apiGet<{
        founders: {
          displayName: string;
          avatarR2Key: string | null;
          isYou?: boolean;
        }[];
      }>("/v1/community/directory", token);
      if (!dir.ok || cancelled) return;
      const self =
        dir.data.founders.find((f) => f.isYou) ?? dir.data.founders[0];
      if (!self) return;
      setSelfDisplayName(self.displayName || "Robin");
      setSelfAvatarKey(self.avatarR2Key?.trim() || null);
    })();
    return () => {
      cancelled = true;
    };
  }, [token, gate, active, communityFocusNonce]);

  useEffect(() => {
    if (!token || !selfAvatarKey) {
      setSelfAvatarUrl(null);
      return;
    }
    let revoked: string | null = null;
    let cancelled = false;
    void (async () => {
      const res = await fetch(
        `/v1/community/media?key=${encodeURIComponent(selfAvatarKey)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok || cancelled) return;
      const blob = await res.blob();
      if (cancelled) return;
      const url = URL.createObjectURL(blob);
      revoked = url;
      setSelfAvatarUrl(url);
    })();
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
      setSelfAvatarUrl(null);
    };
  }, [token, selfAvatarKey]);

  useEffect(() => {
    if (!profileMenuOpen && !mobileNavOpen && !productMenuId) return;
    const onPointer = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        profileMenuOpen &&
        profileMenuRef.current &&
        !profileMenuRef.current.contains(target)
      ) {
        setProfileMenuOpen(false);
      }
      if (
        productMenuId &&
        !(target instanceof Element && target.closest(".op-product-row"))
      ) {
        setProductMenuId(null);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setProfileMenuOpen(false);
        setMobileNavOpen(false);
        setProductMenuId(null);
      }
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [profileMenuOpen, mobileNavOpen, productMenuId]);

  useEffect(() => {
    if (!token) {
      setLogoPreviews((prev) => {
        for (const url of Object.values(prev)) URL.revokeObjectURL(url);
        return {};
      });
      return;
    }
    let cancelled = false;
    const withLogo = products.filter((p) => p.logoR2Key);
    (async () => {
      const next: Record<string, string> = {};
      await Promise.all(
        withLogo.map(async (p) => {
          const res = await fetch(`/v1/products/${p.id}/logo`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!res.ok) return;
          const blob = await res.blob();
          if (cancelled) return;
          next[p.id] = URL.createObjectURL(blob);
        }),
      );
      if (cancelled) {
        for (const url of Object.values(next)) URL.revokeObjectURL(url);
        return;
      }
      setLogoPreviews((prev) => {
        for (const url of Object.values(prev)) URL.revokeObjectURL(url);
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [token, products]);

  async function uploadProductLogo(productId: string, file: File) {
    if (!token) return;
    if (!file.type.startsWith("image/")) {
      setLogoNotice("Choose an image file");
      return;
    }
    setLogoBusyId(productId);
    setLogoNotice("");
    const res = await apiPutBlob<{ logoR2Key: string }>(
      `/v1/products/${productId}/logo`,
      token,
      file,
      file.type || "image/png",
    );
    setLogoBusyId(null);
    if (!res.ok) {
      setLogoNotice(`Upload failed (${res.status})`);
      return;
    }
    setProducts((prev) =>
      prev.map((p) =>
        p.id === productId ? { ...p, logoR2Key: res.data.logoR2Key } : p,
      ),
    );
    setLogoNotice(`Logo saved for ${productId}`);
  }

  async function acceptQuest(id: string) {
    if (!token) return;
    setBusyQuestId(id);
    const res = await apiPost<{ quest: Quest }>(`/v1/quests/${id}/accept`, token);
    setBusyQuestId(null);
    if (!res.ok) {
      setQuestNotice(`Assign failed: ${res.status} ${res.error}`);
      return;
    }
    setQuestNotice(`Assigned: ${res.data.quest.title}`);
    await refreshGoalsQuests(token);
  }

  async function assignSeedQuest(seed: SeedQuest) {
    if (!token) return;
    const xp =
      ledgerXp != null
        ? ledgerXp
        : quests.filter((q) => q.status === "completed").length * XP_PER_QUEST;
    const level = lookoutLevelFromXp(xp);
    if (isMinLevelLocked(seed.minLevel, level)) {
      setQuestNotice(`Reach lookout L${seed.minLevel} to unlock.`);
      return;
    }
    if (seed.requires) {
      const parent = SEED_QUEST_CATALOG.find((s) => s.id === seed.requires);
      const parentDone =
        parent &&
        matchSeedInQuests(parent, quests)?.status === "completed";
      if (!parentDone) {
        setQuestNotice(`Finish ${seed.requires} first.`);
        return;
      }
    }

    setBusyQuestId(seed.id);
    const existing = matchSeedInQuests(seed, quests);
    let questId = existing?.id;

    if (!questId) {
      const created = await apiPost<{ quest: Quest }>("/v1/quests", token, {
        title: seed.title,
        description: seedDescription(seed),
        level: apiTierFromMinLevel(seed.minLevel),
        status: "suggested",
        executableBy: seed.executableBy ?? "either",
        templateId: seed.id,
        templateVersion: CATALOG_VERSION,
      });
      if (!created.ok) {
        setBusyQuestId(null);
        setQuestNotice(`Create failed: ${created.status} ${created.error}`);
        return;
      }
      questId = created.data.quest.id;
    }

    if (
      existing &&
      (existing.status === "accepted" ||
        existing.status === "in_progress" ||
        existing.status === "proof_submitted" ||
        existing.status === "completed")
    ) {
      setBusyQuestId(null);
      setQuestNotice(
        existing.status === "completed"
          ? "Already completed."
          : "Already on your active board.",
      );
      await refreshGoalsQuests(token);
      return;
    }

    const res = await apiPost<{ quest: Quest }>(
      `/v1/quests/${questId}/accept`,
      token,
    );
    setBusyQuestId(null);
    if (!res.ok) {
      setQuestNotice(`Assign failed: ${res.status} ${res.error}`);
      await refreshGoalsQuests(token);
      return;
    }
    setQuestNotice(`Assigned: ${seed.title}`);
    setMyActiveOpen(true);
    await refreshGoalsQuests(token);
  }

  async function submitProof(id: string) {
    if (!token) return;
    const evidenceRef = (proofDrafts[id] ?? "").trim();
    if (!evidenceRef) {
      setQuestNotice("Paste a proof URL first.");
      return;
    }
    setBusyQuestId(id);
    const res = await apiPost<{ quest: Quest }>(`/v1/quests/${id}/proof`, token, {
      evidenceRef,
    });
    setBusyQuestId(null);
    if (!res.ok) {
      setQuestNotice(`Proof failed: ${res.status} ${res.error}`);
      return;
    }
    setQuestNotice("Proof linked — confirm “is this true?”");
    await refreshGoalsQuests(token);
  }

  async function confirmQuest(id: string, isTrue: boolean) {
    if (!token) return;
    setBusyQuestId(id);
    const res = await apiPost<{
      confirmed: boolean;
      completed: boolean;
      xpAwarded: boolean;
      xp?: { id: string; amount: number };
      communityShared?: boolean;
      message?: string;
    }>(`/v1/quests/${id}/confirm`, token, { isTrue });
    setBusyQuestId(null);
    if (!res.ok) {
      setQuestNotice(`Confirm failed: ${res.status} ${res.error}`);
      return;
    }
    if (!isTrue) {
      setQuestNotice("Marked not true — no XP.");
    } else if (res.data.xpAwarded) {
      const shared = res.data.communityShared
        ? " Shared to community."
        : "";
      setQuestNotice(
        `Confirmed — +${res.data.xp?.amount ?? 40} XP awarded.${shared}`,
      );
      setRewardBurstKey((k) => k + 1);
      setShowRewardBurst(true);
      window.setTimeout(() => setShowRewardBurst(false), 780);
    } else {
      const shared = res.data.communityShared
        ? " Shared to community."
        : "";
      setQuestNotice(
        `${res.data.message ?? "Confirmed — XP already awarded (no double-pay)."}${shared}`,
      );
    }
    await refreshGoalsQuests(token);
  }

  async function leaveQuest(id: string) {
    if (!token) return;
    setBusyQuestId(id);
    const res = await apiPost<{ quest: Quest; left: boolean }>(
      `/v1/quests/${id}/leave`,
      token,
    );
    setBusyQuestId(null);
    if (!res.ok) {
      setQuestNotice(`Leave failed: ${res.status} ${res.error}`);
      return;
    }
    setExpandedActiveId((cur) => (cur === id ? null : cur));
    setQuestNotice(`Left quest — back in catalog.`);
    await refreshGoalsQuests(token);
  }

  const selectedProduct =
    productFilter === "all"
      ? null
      : products.find((p) => p.id === productFilter) ?? null;

  const crumbProduct = selectedProduct?.name ?? "All products";
  const crumbPage =
    active === "monitoring"
      ? monitoringTab === "ops"
        ? "Monitoring / Operations"
        : monitoringTab === "connections"
          ? "Monitoring / Connections"
          : monitoringTab === "sessions"
            ? "Monitoring / Sessions"
            : "Monitoring / Growth"
      : active === "community"
        ? communityFocus === "profile"
          ? "Community / My profile"
          : communityFocus === "lookouts"
            ? "Community / Lookouts"
            : communityFocus === "products"
              ? "Community / Products"
              : communityFocus === "new"
                ? "Community / New joins"
                : "Community / Feed"
        : active === "agents"
          ? "Settings / Agents · MCP"
          : active === "admin"
            ? "Admin"
          : active === "product_settings"
            ? "Product settings"
            : NAV.find((n) => n.id === active)?.label ?? active;

  const hasLitConnections = integrations.some(
    (i) =>
      i.status === "connected" ||
      i.status === "stale" ||
      i.status === "error",
  );

  const bufferConnection = integrations.find((i) => i.provider === "buffer");
  const bufferStatsRaw =
    bufferConnection &&
    (bufferConnection.status === "connected" ||
      bufferConnection.status === "stale" ||
      bufferConnection.status === "error")
      ? parseBufferStats(bufferConnection.metaJson)
      : null;
  const bufferMetricWindow: BufferMetricWindow =
    metricWindow === "month"
      ? "month"
      : metricWindow === "week"
        ? "week"
        : "day";
  const bufferGrowth = bufferStatsRaw
    ? bufferStatsForProductScope(
        bufferStatsRaw,
        productFilter === "all" ? null : selectedProduct,
        bufferMetricWindow,
      )
    : null;

  const instantlyConnection = integrations.find(
    (i) => i.provider === "instantly",
  );
  const instantlyStatsRaw =
    instantlyConnection &&
    (instantlyConnection.status === "connected" ||
      instantlyConnection.status === "stale" ||
      instantlyConnection.status === "error")
      ? parseInstantlyStats(instantlyConnection.metaJson)
      : null;
  const instantlyGrowth = instantlyStatsRaw
    ? instantlyGrowthForProductScope(
        instantlyStatsRaw,
        productFilter === "all" ? null : selectedProduct,
      )
    : null;

  const gaConnection = integrations.find((i) => i.provider === "ga");
  const gscConnection = integrations.find((i) => i.provider === "gsc");
  const gaMeta =
    gaConnection &&
    (gaConnection.status === "connected" ||
      gaConnection.status === "stale" ||
      gaConnection.status === "error")
      ? parseGaMeta(gaConnection.metaJson)
      : null;
  const gscMeta =
    gscConnection &&
    (gscConnection.status === "connected" ||
      gscConnection.status === "stale" ||
      gscConnection.status === "error")
      ? parseGscMeta(gscConnection.metaJson)
      : null;
  const googleGrowth = gaMeta
    ? googleGrowthForProductScope(
        gaMeta,
        productFilter === "all" ? null : selectedProduct,
        metricWindow,
      )
    : null;
  const gscGrowth = gscMeta
    ? gscGrowthForProductScope(
        gscMeta,
        productFilter === "all" ? null : selectedProduct,
        metricWindow,
      )
    : null;

  const ahrefsConnection = integrations.find((i) => i.provider === "ahrefs");
  const ahrefsMeta =
    ahrefsConnection &&
    (ahrefsConnection.status === "connected" ||
      ahrefsConnection.status === "stale" ||
      ahrefsConnection.status === "error")
      ? parseAhrefsMeta(ahrefsConnection.metaJson)
      : null;
  const ahrefsGrowth = ahrefsMeta
    ? ahrefsGrowthForProductScope(
        ahrefsMeta,
        productFilter === "all" ? null : selectedProduct,
      )
    : null;

  const posthogConnection = integrations.find((i) => i.provider === "posthog");
  const posthogMeta =
    posthogConnection &&
    (posthogConnection.status === "connected" ||
      posthogConnection.status === "stale" ||
      posthogConnection.status === "error")
      ? parsePostHogMeta(posthogConnection.metaJson)
      : null;
  const posthogGrowth = posthogMeta
    ? posthogGrowthForProductScope(
        posthogMeta,
        productFilter === "all" ? null : selectedProduct,
      )
    : null;

  function goToConnections(openPicker = false) {
    setMonitoringTab("connections");
    if (openPicker) {
      setDraftProvider(null);
      setTuneProvider(null);
      setConnPickerOpen(true);
    }
  }

  function openMyProfile() {
    setActive("community");
    setCommunityFocus("profile");
    setCommunityFocusNonce((n) => n + 1);
    setMobileNavOpen(false);
    setProfileMenuOpen(false);
  }

  function openAddProduct() {
    setActive("community");
    setCommunityFocus("profile");
    setCommunityFocusNonce((n) => n + 1);
    setNewStartupNonce((n) => n + 1);
    setMobileNavOpen(false);
    setProfileMenuOpen(false);
  }

  function openAgentsSettings() {
    setActive("agents");
    setMobileNavOpen(false);
    setProfileMenuOpen(false);
  }

  function openAdmin() {
    setActive("admin");
    setMobileNavOpen(false);
    setProfileMenuOpen(false);
  }

  function openProductSettings(productId: string) {
    setSettingsProductId(productId);
    setProductFilter(productId);
    setMetricProductId(productId);
    setActive("product_settings");
    setProductMenuId(null);
    setMobileNavOpen(false);
  }

  function openDigestTarget(n: DigestNextAction) {
    if (n.productId) {
      setProductFilter(n.productId);
      setMetricProductId(n.productId);
    }
    if (n.targetType === "quest" && n.targetId) {
      setActive("quests");
      setMyActiveOpen(true);
      const q = quests.find((item) => item.id === n.targetId);
      const seed = q ? findSeedForQuest(q) : null;
      if (seed && q?.status === "suggested") {
        const band = decadeStart(seed.minLevel);
        setExpandedDecade(band);
        setExpandedCatalogId(seed.id);
        setExpandedActiveId(null);
      } else {
        setExpandedActiveId(n.targetId);
        setExpandedCatalogId(null);
      }
      setMobileNavOpen(false);
      window.requestAnimationFrame(() => {
        document
          .getElementById(`op-quest-${n.targetId}`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      return;
    }
    if (n.targetType === "goal") {
      setActive("home");
      setMobileNavOpen(false);
      window.requestAnimationFrame(() => {
        document
          .getElementById("op-home-goals")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
      return;
    }
    if (n.targetType === "connection") {
      setActive("monitoring");
      setMonitoringTab("connections");
      setMobileNavOpen(false);
      return;
    }
    if (n.targetType === "product" && (n.productId || n.targetId)) {
      openProductSettings(n.productId || n.targetId!);
      return;
    }
    setActive("quests");
    setMobileNavOpen(false);
  }

  function goNav(id: NavId) {
    setActive(id);
    if (id === "community") {
      setCommunityFocus("feed");
      setCommunityFocusNonce((n) => n + 1);
    } else {
      setCommunityFocus("feed");
    }
    if (id === "monitoring") {
      setMonitoringTab("growth");
    }
    if (id !== "product_settings") setSettingsProductId(null);
    setMobileNavOpen(false);
  }

  function goCommunityDest(dest: "feed" | "lookouts" | "products") {
    setActive("community");
    setCommunityFocus(dest);
    setCommunityFocusNonce((n) => n + 1);
    setMobileNavOpen(false);
  }

  function goMonitoringTab(tab: MonitoringTab) {
    setActive("monitoring");
    setMonitoringTab(tab);
    setMobileNavOpen(false);
  }

  useEffect(() => {
    if (!token || gate !== "open") {
      setInviteXpClaimed(false);
      return;
    }
    let cancelled = false;
    void (async () => {
      const res = await apiGet<{
        xpClaimed: boolean;
        xpAmount: number;
        text: string;
      }>("/v1/community/invite", token);
      if (!res.ok || cancelled) return;
      setInviteXpClaimed(Boolean(res.data.xpClaimed));
    })();
    return () => {
      cancelled = true;
    };
  }, [token, gate]);

  async function shareCommunityInvite() {
    if (!token || inviteBusy) return;
    setInviteBusy(true);
    const minted = await apiPost<{
      text: string;
      url: string;
      xpAwarded: boolean;
      alreadyClaimed: boolean;
      xpAmount: number;
    }>("/v1/community/invite/share", token, {});
    if (!minted.ok) {
      setInviteNotice(`Invite failed: ${minted.error}`);
      window.setTimeout(() => setInviteNotice(""), 2800);
      setInviteBusy(false);
      return;
    }
    const text = minted.data.text || COMMUNITY_INVITE_TEXT;
    const url = minted.data.url || "https://outpost16.com/join";
    let shared = false;
    try {
      if (typeof navigator.share === "function") {
        await navigator.share({
          title: "Outpost16",
          text,
          url,
        });
        shared = true;
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        setInviteBusy(false);
        return;
      }
    }
    if (!shared) {
      try {
        await navigator.clipboard.writeText(text);
        shared = true;
      } catch {
        setInviteNotice("Couldn’t copy invite");
        window.setTimeout(() => setInviteNotice(""), 2200);
        setInviteBusy(false);
        return;
      }
    }

    setInviteBusy(false);
    if (minted.data.alreadyClaimed) setInviteXpClaimed(true);
    setInviteNotice(
      minted.data.alreadyClaimed
        ? shared
          ? "Invite copied · XP already earned when someone joined"
          : "Invite ready"
        : shared
          ? "Invite copied — XP when they join"
          : "Invite ready — XP when they join",
    );
    window.setTimeout(() => setInviteNotice(""), 3200);
  }

  function selectProduct(id: string) {
    setProductFilter(id);
    if (id !== "all") setMetricProductId(id);
  }

  function focusNewQuest() {
    setActive("quests");
    setMobileNavOpen(false);
    window.requestAnimationFrame(() => {
      questCatalogRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }

  const avatarMark = selfDisplayName.trim().charAt(0).toUpperCase() || "R";

  function fmtNull(n: number | null | undefined): string {
    return n === null || n === undefined ? "—" : String(n);
  }

  function fmtRatePct(rate: number | null | undefined): string {
    if (rate == null || !Number.isFinite(rate)) return "—";
    return `${Math.round(rate * 1000) / 10}%`;
  }

  function fmtSynced(iso: string | null | undefined, stale?: boolean): string {
    if (!iso) return "";
    const when = new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
    return ` · synced ${when}${stale ? " · stale" : ""}`;
  }

  function fmtSyncedRelative(
    iso: string | null | undefined,
    stale?: boolean,
  ): string {
    if (!iso) return "Not synced yet";
    const age = Date.now() - Date.parse(iso);
    if (!Number.isFinite(age)) return "Synced —";
    const mins = Math.round(age / 60000);
    let rel: string;
    if (mins < 1) rel = "just now";
    else if (mins < 60) rel = `${mins}m ago`;
    else if (mins < 48 * 60) rel = `${Math.round(mins / 60)}h ago`;
    else rel = `${Math.round(mins / (60 * 24))}d ago`;
    return `Synced ${rel}${stale ? " · stale" : ""}`;
  }

  function fmtDelta(n: number | null | undefined): string | null {
    if (n == null) return null;
    return `${n > 0 ? "+" : ""}${n}`;
  }

  function syncFreshness(
    iso: string | null | undefined,
    stale?: boolean,
  ): GrowthFreshness {
    if (stale) return "stale";
    if (!iso) return "quiet";
    const age = Date.now() - Date.parse(iso);
    if (!Number.isFinite(age)) return "quiet";
    // Align with PostHog API freshness (6h live / 48h quiet).
    if (age < 6 * 60 * 60 * 1000) return "live";
    if (age < 48 * 60 * 60 * 1000) return "quiet";
    return "stale";
  }

  function activationWindowCounts() {
    const pg = posthogGrowth;
    if (!pg || pg.unmatched) return null;
    if (metricWindow === "day") {
      return {
        signups: pg.signupCount1d,
        firstValue: pg.firstValueCount1d,
        priorSignups: null as number | null,
        priorFirst: null as number | null,
        windowLabel: "1d",
        comparisonLabel: null as string | null,
      };
    }
    if (metricWindow === "week") {
      return {
        signups: pg.signupCount7d,
        firstValue: pg.firstValueCount7d,
        priorSignups: pg.signupCount7dPrior,
        priorFirst: pg.firstValueCount7dPrior,
        windowLabel: "7d",
        comparisonLabel: "vs prior 7d",
      };
    }
    return {
      signups: pg.signupCount30d,
      firstValue: pg.firstValueCount30d,
      priorSignups: pg.signupCount30dPrior,
      priorFirst: pg.firstValueCount30dPrior,
      windowLabel: "30d",
      comparisonLabel: "vs prior 30d",
    };
  }

  function buildGrowthTicker(): GrowthTickerItem[] {
    const items: GrowthTickerItem[] = [];
    const act = activationWindowCounts();
    if (posthogGrowth && !posthogGrowth.unmatched && act) {
      const signupDelta =
        act.priorSignups != null && act.signups != null
          ? act.signups - act.priorSignups
          : null;
      items.push({
        id: "ph-signups",
        source: "PostHog",
        label: `Signups ${act.windowLabel}`,
        value: fmtNull(act.signups),
        delta: signupDelta,
        watchLabel: act.comparisonLabel ? "no prior yet" : "watching",
      });
      if (posthogGrowth.exceptionCount7d != null) {
        items.push({
          id: "ph-exc",
          source: "PostHog",
          label: "Exceptions 7d",
          value: fmtNull(posthogGrowth.exceptionCount7d),
          polarity: "lower-better",
          watchLabel: "no prior yet",
        });
      }
    }
    if (googleGrowth && !googleGrowth.unmatched) {
      const sessDelta =
        googleGrowth.sessions7d != null &&
        googleGrowth.sessions7dPrior != null
          ? googleGrowth.sessions7d - googleGrowth.sessions7dPrior
          : null;
      items.push({
        id: "ga-sess",
        source: "Google",
        label: "Sessions 7d",
        value: fmtNull(googleGrowth.sessions7d),
        delta: sessDelta,
        watchLabel: "no prior yet",
      });
    }
    if (gscGrowth && !gscGrowth.unmatched) {
      const clickDelta =
        gscGrowth.clicks != null && gscGrowth.clicksPrior != null
          ? gscGrowth.clicks - gscGrowth.clicksPrior
          : null;
      items.push({
        id: "gsc-clicks",
        source: "Search",
        label: "Clicks",
        value: fmtNull(gscGrowth.clicks),
        delta: clickDelta,
        watchLabel: "no prior yet",
      });
    }
    if (ahrefsGrowth && !ahrefsGrowth.unmatched) {
      items.push({
        id: "ah-dr",
        source: "Ahrefs",
        label: "Domain Rating",
        value: fmtNull(ahrefsGrowth.domainRating),
        delta:
          ahrefsGrowth.drDeltaSinceBaseline ??
          ahrefsGrowth.drDelta30d ??
          seriesSpanDelta(ahrefsGrowth.series),
      });
    }
    if (bufferGrowth && !bufferGrowth.unmatched) {
      const impDelta =
        bufferGrowth.impressions != null &&
        bufferGrowth.impressionsPrior != null
          ? bufferGrowth.impressions - bufferGrowth.impressionsPrior
          : null;
      const queueBand = bufferQueueBand(
        bufferGrowth.scheduledCount,
        bufferGrowth.channelCount,
      );
      items.push({
        id: "buf-queue",
        source: "Buffer",
        label: "Queued",
        value: `${fmtNull(bufferGrowth.scheduledCount)}${bufferGrowth.scheduledTruncated ? "+" : ""}`,
        watchLabel:
          bufferQueueBandLabel(queueBand, bufferGrowth.channelCount) ??
          "watching",
      });
      items.push({
        id: "buf-sent",
        source: "Buffer",
        label: "Posts",
        value: `${fmtNull(bufferGrowth.published)}${bufferGrowth.publishedTruncated ? "+" : ""}`,
        watchLabel: "no prior yet",
      });
      items.push({
        id: "buf-imp",
        source: "Buffer",
        label: "Impressions",
        value: fmtNull(bufferGrowth.impressions),
        delta: impDelta,
        watchLabel: "no prior yet",
      });
    }
    if (instantlyGrowth) {
      if (instantlyGrowth.mode === "warmup") {
        items.push({
          id: "in-health",
          source: "Instantly",
          label: "Avg health",
          value: fmtNull(instantlyGrowth.avgHealthScore),
          delta: seriesSpanDelta(instantlyGrowth.series),
          watchLabel: "no prior yet",
        });
      } else {
        items.push({
          id: "in-reply",
          source: "Instantly",
          label: "Replied 7d",
          value: fmtNull(instantlyGrowth.replied7d),
          watchLabel: "no prior yet",
        });
        items.push({
          id: "in-sent",
          source: "Instantly",
          label: "Sent 7d",
          value: fmtNull(instantlyGrowth.sent7d),
          watchLabel: "no prior yet",
        });
      }
    }
    return items.filter((i) => i.value !== "—");
  }

  function posthogToolHref(): string {
    const project = posthogGrowth?.projects?.[0];
    if (project?.host && project?.projectId) {
      const host = project.host.replace(/\/$/, "");
      return `${host}/project/${project.projectId}`;
    }
    return "https://eu.posthog.com";
  }

  const completedCount = quests.filter((q) => q.status === "completed").length;
  const lookoutXp =
    ledgerXp != null ? ledgerXp : completedCount * XP_PER_QUEST;
  const lookoutLevel = lookoutLevelFromXp(lookoutXp);
  const suggestedQuests = quests.filter((q) => q.status === "suggested");
  const activeQuests = quests.filter((q) => {
    const statusOk =
      q.status === "accepted" ||
      q.status === "in_progress" ||
      q.status === "proof_submitted";
    if (!statusOk) return false;
    if (assigneeFilter === "ALL") return true;
    if (assigneeFilter === "agent") {
      return (
        q.assigneeKind === "agent" ||
        q.executableBy === "agent" ||
        q.completedBy === "agent"
      );
    }
    if (assigneeFilter === "human") {
      return (
        (q.assigneeKind === "human" || q.assigneeKind == null) &&
        q.executableBy !== "agent"
      );
    }
    return (q.executableBy || "either") === "either";
  });

  useEffect(() => {
    if (activeQuests.some((q) => q.status === "proof_submitted")) {
      setMyActiveOpen(true);
    } else if (activeQuests.length === 0) {
      setMyActiveOpen(false);
    }
  }, [activeQuests]);

  const canAdmin =
    me?.kind === "founder" ||
    me?.role === "owner" ||
    Boolean(me?.user?.isPlatformAdmin);

  if (gate !== "open") {
    return (
      <div className="op-gate" aria-label="Outpost16 login">
        <div className="op-gate-card">
          <div className="op-gate-brand">
            <img
              src="/brand/outpost16-lookout-app-mark.png"
              alt=""
              width={48}
              height={48}
            />
            <p className="op-wordmark">
              outpost<span className="op-brand-number">16</span>
            </p>
          </div>
          {gate === "checking" ? (
            <p className="op-gate-copy">Checking session…</p>
          ) : joinMode ? (
            <>
              <h1>Join the lookout</h1>
              {joinPreview ? (
                <p className="op-gate-copy">
                  {joinPreview.revoked
                    ? "This invite was revoked."
                    : joinPreview.expired
                      ? "This invite has expired."
                      : joinPreview.accepted
                        ? "Already accepted — sign in with your email."
                        : `You're invited to ${joinPreview.orgName} as ${joinPreview.role}.`}
                </p>
              ) : (
                <p className="op-gate-copy">
                  {joinPreviewError
                    ? joinPreviewError
                    : joinInvite
                      ? "Loading invite…"
                      : "Missing invite token. Ask for a fresh invite link."}
                </p>
              )}
              {joinInvite &&
              joinPreview &&
              !joinPreview.revoked &&
              !joinPreview.expired ? (
                <>
                  <label className="op-gate-label">
                    Email
                    <input
                      className="op-field"
                      type="email"
                      value={emailDraft}
                      onChange={(e) => setEmailDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") void requestMagicLink();
                      }}
                      autoComplete="email"
                      autoFocus
                      readOnly={
                        Boolean(joinPreview.email) && joinPreview.email !== "*"
                      }
                    />
                  </label>
                  {gateError ? (
                    <p className="op-notice err" role="alert">
                      {gateError}
                    </p>
                  ) : null}
                  {magicSent ? (
                    <p className="op-notice" role="status">
                      Check your inbox for a sign-in link.
                      {magicVerifyUrl ? (
                        <>
                          {" "}
                          <a href={magicVerifyUrl}>Open link (dev)</a>
                        </>
                      ) : null}
                    </p>
                  ) : (
                    <button
                      type="button"
                      className="op-primary"
                      disabled={gateBusy}
                      onClick={() => void requestMagicLink()}
                    >
                      {gateBusy ? "Sending…" : "Send sign-in link"}
                    </button>
                  )}
                </>
              ) : null}
              <p className="op-gate-footnote">
                <a href="/app">Already a member? Sign in</a>
                {" · "}
                <a href="/">← outpost16.com</a>
              </p>
            </>
          ) : (
            <>
              <h1>Private lookout</h1>
              <p className="op-gate-copy">
                Invite-only. Enter the email on your invite to get a magic
                sign-in link.
              </p>
              <label className="op-gate-label">
                Email
                <input
                  className="op-field"
                  type="email"
                  value={emailDraft}
                  onChange={(e) => setEmailDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void requestMagicLink();
                  }}
                  autoComplete="email"
                  autoFocus
                />
              </label>
              {gateError ? (
                <p className="op-notice err" role="alert">
                  {gateError}
                </p>
              ) : null}
              {magicSent ? (
                <p className="op-notice" role="status">
                  Check your inbox for a sign-in link.
                  {magicVerifyUrl ? (
                    <>
                      {" "}
                      <a href={magicVerifyUrl}>Open link (dev)</a>
                    </>
                  ) : null}
                </p>
              ) : (
                <button
                  type="button"
                  className="op-primary"
                  disabled={gateBusy}
                  onClick={() => void requestMagicLink()}
                >
                  {gateBusy ? "Sending…" : "Email me a link"}
                </button>
              )}
              <button
                type="button"
                className="op-ghost op-gate-advanced"
                onClick={() => setShowAdvancedToken((v) => !v)}
              >
                {showAdvancedToken ? "Hide" : "Advanced"}: API token
              </button>
              {showAdvancedToken ? (
                <>
                  <label className="op-gate-label">
                    API / founder token
                    <input
                      className="op-field"
                      type="password"
                      value={tokenDraft}
                      onChange={(e) => setTokenDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") void saveToken();
                      }}
                      autoComplete="current-password"
                    />
                  </label>
                  <button
                    type="button"
                    className="op-primary"
                    disabled={gateBusy}
                    onClick={() => void saveToken()}
                  >
                    {gateBusy ? "Connecting…" : "Unlock with token"}
                  </button>
                </>
              ) : null}
              <p className="op-gate-footnote">
                <a href="/">← outpost16.com</a>
                {" · "}
                Have an invite? Use the link from your email.
              </p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div id="outpost-workspace" aria-label="Outpost16 sunset workspace">
      {showRewardBurst ? (
        <div
          key={rewardBurstKey}
          className="op-reward-burst"
          aria-hidden
        />
      ) : null}
      {eventToasts.length > 0 ? (
        <div className="op-toast-stack" aria-live="polite">
          {eventToasts.map((toast) => (
            <div
              key={toast.id}
              className={`op-toast op-toast-${toast.severity}`}
              role="status"
            >
              <div className="op-toast-copy">
                <span className="op-toast-kind">
                  {orgEventKindLabel(toast.kind)}
                </span>
                <strong>{toast.title}</strong>
                {toast.body ? <small>{toast.body}</small> : null}
              </div>
              <button
                type="button"
                className="op-toast-dismiss"
                aria-label="Dismiss alert"
                onClick={() =>
                  setEventToasts((prev) =>
                    prev.filter((t) => t.id !== toast.id),
                  )
                }
              >
                <X size={14} strokeWidth={2} aria-hidden />
              </button>
            </div>
          ))}
        </div>
      ) : null}
      <div className={`op-shell${mobileNavOpen ? " op-shell-nav-open" : ""}`}>
        {mobileNavOpen ? (
          <button
            type="button"
            className="op-nav-backdrop"
            aria-label="Close menu"
            onClick={() => setMobileNavOpen(false)}
          />
        ) : null}

        <aside className="op-side" id="op-side-nav">
          <div className="op-brand op-brand-desktop">
            <button
              type="button"
              className="op-brand-home"
              aria-label="Outpost16 home"
              onClick={() => goNav("home")}
            >
              <span className="op-logo-slot">
                <img
                  src="/brand/outpost16-lookout-app-mark.png"
                  alt=""
                  width={35}
                  height={35}
                />
              </span>
              <span className="op-wordmark">
                outpost<span className="op-brand-number">16</span>
              </span>
            </button>
          </div>

          <div className="op-side-drawer-head">
            <button
              type="button"
              className="op-brand-home"
              aria-label="Outpost16 home"
              onClick={() => {
                goNav("home");
                setMobileNavOpen(false);
              }}
            >
              <span className="op-wordmark">
                outpost<span className="op-brand-number">16</span>
              </span>
            </button>
            <button
              type="button"
              className="op-icon-btn"
              aria-label="Close menu"
              onClick={() => setMobileNavOpen(false)}
            >
              <X aria-hidden />
            </button>
          </div>

          <nav className="op-nav" aria-label="Workspace">
            {NAV.map((item) => (
              <div key={item.id} className="op-nav-group">
                <button
                  type="button"
                  aria-pressed={active === item.id}
                  onClick={() => goNav(item.id)}
                >
                  <item.Icon aria-hidden />
                  {item.label}
                </button>
                {item.id === "monitoring" && active === "monitoring" ? (
                  <div className="op-nav-sub" role="group" aria-label="Monitoring pages">
                    {MONITORING_SUBNAV.map((sub) => (
                      <button
                        key={sub.id}
                        type="button"
                        className="op-nav-sub-btn"
                        aria-pressed={monitoringTab === sub.id}
                        onClick={() => goMonitoringTab(sub.id)}
                      >
                        {sub.label}
                      </button>
                    ))}
                  </div>
                ) : null}
                {item.id === "community" && active === "community" ? (
                  <div className="op-nav-sub" role="group" aria-label="Community pages">
                    {COMMUNITY_SUBNAV.map((sub) => (
                      <button
                        key={sub.id}
                        type="button"
                        className="op-nav-sub-btn"
                        aria-pressed={communityFocus === sub.id}
                        onClick={() => goCommunityDest(sub.id)}
                      >
                        {sub.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
            {canAdmin ? (
              <div className="op-nav-group">
                <button
                  type="button"
                  className="op-nav-btn"
                  aria-pressed={active === "admin"}
                  onClick={() => {
                    setActive("admin");
                    setMobileNavOpen(false);
                  }}
                >
                  <Shield size={18} strokeWidth={1.75} aria-hidden />
                  Admin
                </button>
              </div>
            ) : null}
          </nav>

          <div className="op-divider op-products-desktop">
            <div className="op-space-label">Product spaces</div>
            <nav className="op-nav" aria-label="Products">
              <button
                type="button"
                className="op-product-btn"
                aria-pressed={productFilter === "all"}
                onClick={() => selectProduct("all")}
              >
                <span className="op-product-logo" aria-hidden>
                  All
                </span>
                All products
              </button>
              {products.map((p) => (
                <div key={p.id} className="op-product-row">
                  <button
                    type="button"
                    className="op-product-menu-btn"
                    aria-label={`${p.name} menu`}
                    aria-expanded={productMenuId === p.id}
                    aria-haspopup="menu"
                    onClick={(e) => {
                      e.stopPropagation();
                      setProductMenuId((cur) => (cur === p.id ? null : p.id));
                    }}
                  >
                    <MoreVertical size={14} aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="op-product-btn"
                    aria-pressed={productFilter === p.id}
                    onClick={() => {
                      setProductMenuId(null);
                      selectProduct(p.id);
                    }}
                  >
                    <span className="op-product-logo" aria-hidden>
                      {logoPreviews[p.id] ? (
                        <img src={logoPreviews[p.id]} alt="" />
                      ) : (
                        p.name.slice(0, 2).toUpperCase()
                      )}
                    </span>
                    {p.name}
                  </button>
                  {productMenuId === p.id ? (
                    <div className="op-product-dropdown" role="menu">
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => openProductSettings(p.id)}
                      >
                        Product settings
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setProductMenuId(null);
                          selectProduct(p.id);
                          setActive("community");
                          setCommunityFocus("feed");
                          setCommunityFocusNonce((n) => n + 1);
                          setMobileNavOpen(false);
                        }}
                      >
                        Open in Community
                      </button>
                    </div>
                  ) : null}
                </div>
              ))}
              <button
                type="button"
                className="op-product-btn op-product-add"
                onClick={openAddProduct}
              >
                <span className="op-product-logo op-product-add-mark" aria-hidden>
                  <Plus size={14} />
                </span>
                Add product
              </button>
            </nav>
          </div>
        </aside>

        <main className="op-content">
          <header className="op-chrome">
            <button
              type="button"
              className="op-icon-btn op-hamburger"
              aria-label={mobileNavOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileNavOpen}
              aria-controls="op-side-nav"
              onClick={() => setMobileNavOpen((o) => !o)}
            >
              {mobileNavOpen ? <X aria-hidden /> : <Menu aria-hidden />}
            </button>

            <div className="op-product-chrome">
              <label className="op-product-select-wrap">
                <span className="visually-hidden">Product</span>
                <select
                  className="op-product-select"
                  value={productFilter}
                  onChange={(e) => selectProduct(e.target.value)}
                  aria-label="Product spaces"
                >
                  <option value="all">All products</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
              {productFilter !== "all" ? (
                <button
                  type="button"
                  className="op-icon-btn op-product-settings-mobile"
                  aria-label="Product settings"
                  onClick={() => openProductSettings(productFilter)}
                >
                  <MoreVertical size={16} aria-hidden />
                </button>
              ) : null}
            </div>

            <span className="op-crumb">
              {crumbProduct} / {crumbPage}
            </span>

            <div className="op-chrome-actions">
              {active === "quests" ? (
                <button
                  type="button"
                  className="op-primary"
                  onClick={focusNewQuest}
                >
                  + New quest
                </button>
              ) : null}
              {token ? (
                <button
                  type="button"
                  className={
                    inviteXpClaimed
                      ? "op-btn op-invite-btn"
                      : "op-primary op-invite-btn"
                  }
                  disabled={inviteBusy}
                  onClick={() => void shareCommunityInvite()}
                  title={
                    inviteXpClaimed
                      ? "Copy or share your invite link"
                      : `Invite a friend · +${COMMUNITY_INVITE_XP} XP once`
                  }
                >
                  <UserPlus size={15} aria-hidden />
                  <span>Invite</span>
                  {!inviteXpClaimed ? (
                    <span className="op-invite-xp">+{COMMUNITY_INVITE_XP} XP</span>
                  ) : null}
                </button>
              ) : null}
              <div className="op-profile-menu" ref={profileMenuRef}>
                <button
                  type="button"
                  className="op-avatar-btn"
                  aria-label={`${selfDisplayName} menu`}
                  aria-expanded={profileMenuOpen}
                  aria-haspopup="menu"
                  onClick={() => setProfileMenuOpen((o) => !o)}
                >
                  <span
                    className={`op-avatar op-avatar-md${selfAvatarUrl ? " op-avatar-photo" : ""}`}
                  >
                    {selfAvatarUrl ? (
                      <img
                        className="op-avatar-img"
                        src={selfAvatarUrl}
                        alt=""
                      />
                    ) : (
                      avatarMark
                    )}
                  </span>
                </button>
                {profileMenuOpen ? (
                  <div className="op-profile-dropdown" role="menu">
                    <div className="op-profile-dropdown-meta">
                      <strong>{selfDisplayName}</strong>
                      <small>
                        L{lookoutLevel} · {lookoutXp} XP
                        {token ? " · live D1" : " · connect token"}
                      </small>
                    </div>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={openMyProfile}
                    >
                      My profile
                    </button>
                    {token ? (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={openAgentsSettings}
                      >
                        Agents / MCP
                      </button>
                    ) : null}
                    {token && canAdmin ? (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={openAdmin}
                      >
                        Admin
                      </button>
                    ) : null}
                    {token ? (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setProfileMenuOpen(false);
                          void clearToken();
                        }}
                      >
                        Sign out
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          </header>

          {(status || homeNotice || questNotice || logoNotice || inviteNotice) && (
            <p
              className={
                status.startsWith("Auth") || status.includes("failed")
                  ? "op-notice err"
                  : "op-notice"
              }
              role="status"
            >
              {status || homeNotice || questNotice || logoNotice || inviteNotice}
            </p>
          )}

          <div className="op-board">
            {token && active === "home" ? (
              <section className="op-pane">
                <div className="op-home-banner">
                  <div className="op-home-embers" aria-hidden />
                  <div className="op-home-intro">
                    <small>YOUR GROWTH LOOKOUT</small>
                    <h1>Keep growth in sight.</h1>
                  </div>
                  <form
                    className="op-prompt"
                    onSubmit={(e) => {
                      e.preventDefault();
                      const intention = promptDraft.trim();
                      if (!intention) {
                        setHomeNotice("Describe what you want to move forward.");
                        return;
                      }
                      void (async () => {
                        const res = await apiPost<{
                          plan: { id: string; message: string };
                        }>("/v1/plans", token, { intention });
                        if (!res.ok) {
                          setHomeNotice(res.error);
                          return;
                        }
                        setHomeNotice(res.data.plan.message);
                        setPromptDraft("");
                        const ev = await apiGet<{ events: OrgEvent[] }>(
                          "/v1/events",
                          token,
                        );
                        if (ev.ok) setOrgEvents(ev.data.events);
                      })();
                    }}
                  >
                    <label htmlFor="op-prompt-text">
                      What would you like to move forward?
                    </label>
                    <textarea
                      id="op-prompt-text"
                      rows={2}
                      value={promptDraft}
                      onChange={(e) => setPromptDraft(e.target.value)}
                      placeholder="Suggest a next quest after zero Instantly replies…"
                    />
                    <div className="op-prompt-bottom">
                      <span className="op-tag">
                        <Sparkles size={12} aria-hidden />
                        Plan first · BYOA via MCP
                      </span>
                      <button className="op-primary" type="submit">
                        Preview a plan ↑
                      </button>
                    </div>
                  </form>
                </div>

                {struggleDigest ? (
                  <>
                    <div className="op-section-title">
                      <h2>Pressure digest</h2>
                      <span className="op-tag">Captain&apos;s log</span>
                    </div>
                    <div className="op-digest">
                      <div className="op-digest-embers" aria-hidden />
                      <div className="op-digest-head">
                        <h3>{struggleDigest.headline}</h3>
                        {struggleDigest.topProductName ? (
                          <span className="op-tag">
                            {struggleDigest.topProductName}
                          </span>
                        ) : null}
                      </div>
                      <p className="op-digest-prose">
                        {struggleDigest.summary || struggleDigest.headline}
                      </p>

                      {struggleDigest.nextAgent ||
                      (struggleDigest.nextFounder?.length ?? 0) > 0 ? (
                        <div className="op-digest-next">
                          <strong className="op-digest-next-label">
                            Do next
                          </strong>
                          <ul className="op-digest-actions">
                            {(struggleDigest.nextFounder ?? []).map((n, i) => (
                              <li key={`founder-${i}`}>
                                <button
                                  type="button"
                                  className="op-digest-action"
                                  onClick={() => openDigestTarget(n)}
                                >
                                  <span className="op-digest-role is-you">
                                    You
                                  </span>
                                  <span className="op-digest-action-text">
                                    {n.action}
                                  </span>
                                </button>
                              </li>
                            ))}
                            {struggleDigest.nextAgent ? (
                              <li>
                                <button
                                  type="button"
                                  className="op-digest-action"
                                  onClick={() =>
                                    openDigestTarget(struggleDigest.nextAgent!)
                                  }
                                >
                                  <span className="op-digest-role is-agent">
                                    Agent
                                  </span>
                                  <span className="op-digest-action-text">
                                    {struggleDigest.nextAgent.action}
                                  </span>
                                </button>
                              </li>
                            ) : null}
                          </ul>
                        </div>
                      ) : null}

                      {struggleDigest.signals.length > 0 ? (
                        <div className="op-digest-why">
                          <button
                            type="button"
                            className="op-linkish op-digest-why-toggle"
                            aria-expanded={digestWhyOpen}
                            onClick={() => setDigestWhyOpen((v) => !v)}
                          >
                            {digestWhyOpen ? "Hide signals" : "Why this?"}
                          </button>
                          {digestWhyOpen ? (
                            <ul className="op-digest-signals">
                              {struggleDigest.signals.slice(0, 5).map((s, i) => (
                                <li key={`${s.kind}-${i}`}>
                                  {s.productName ? (
                                    <strong>{s.productName}</strong>
                                  ) : null}
                                  {s.productName ? " · " : null}
                                  {s.summary}
                                </li>
                              ))}
                            </ul>
                          ) : null}
                          {(struggleDigest.fixtureSignals?.length ?? 0) > 0 ? (
                            <small className="op-digest-fixture">
                              {struggleDigest.fixtureSignals!.length}{" "}
                              fixture/verify signal
                              {struggleDigest.fixtureSignals!.length === 1
                                ? ""
                                : "s"}{" "}
                              demoted
                            </small>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </>
                ) : null}

                <div className="op-section-title">
                  <h2>Events</h2>
                  <span className="op-sample">
                    {loading ? "Loading" : "Live"}
                  </span>
                </div>
                <div className="op-list">
                  {orgEvents.length === 0 ? (
                    <div className="op-list-item">
                      <img
                        className="op-empty-icon"
                        src="/brand/outpost16-empty-inbox-binoculars.png"
                        alt=""
                        width={44}
                        height={44}
                      />
                      <div className="op-item-copy">
                        <h3>No events yet</h3>
                        <small>
                          Follows, agent progress, quest completes, and
                          connector connects land here.
                        </small>
                      </div>
                    </div>
                  ) : (
                    orgEvents.map((item) => (
                      <div
                        key={item.id}
                        className="op-list-item op-inbox-item"
                      >
                        <div className="op-item-copy">
                          <div className="op-event-meta">
                            <span className="op-chip op-event-kind">
                              {orgEventKindLabel(item.kind)}
                            </span>
                            <small>{relativeTimeLabel(item.createdAt)}</small>
                          </div>
                          <h3>{item.title}</h3>
                          {item.body ? <small>{item.body}</small> : null}
                        </div>
                        <button
                          type="button"
                          className="op-chip"
                          onClick={() =>
                            void (async () => {
                              await apiPost(
                                `/v1/events/${item.id}/dismiss`,
                                token,
                              );
                              setOrgEvents((prev) =>
                                prev.filter((a) => a.id !== item.id),
                              );
                              setEventToasts((prev) =>
                                prev.filter((t) => t.id !== item.id),
                              );
                            })()
                          }
                        >
                          Dismiss
                        </button>
                      </div>
                    ))
                  )}
                </div>

                <div className="op-section-title">
                  <h2>Observe → Act</h2>
                  <span className="op-tag">Suggested quests</span>
                </div>
                <div className="op-list">
                  {suggestedQuests.length === 0 ? (
                    <div className="op-list-item">
                      <img
                        className="op-empty-icon"
                        src="/brand/outpost16-no-quests-compass.png"
                        alt=""
                        width={44}
                        height={44}
                      />
                      <div className="op-item-copy">
                        <h3>No suggested quests yet</h3>
                        <small>
                          Create one on Quests, or wait for signal→quest bridge.
                        </small>
                      </div>
                      <button
                        type="button"
                        className="op-btn"
                        onClick={() => setActive("quests")}
                      >
                        Open Quests
                      </button>
                    </div>
                  ) : (
                    suggestedQuests.map((q) => {
                      const locked = isMinLevelLocked(q.level, lookoutLevel);
                      return (
                        <div
                          key={q.id}
                          id={`op-quest-${q.id}`}
                          className={`op-list-item${locked ? " op-quest-locked" : ""}`}
                          title={
                            locked
                              ? `Reach L${Math.max(1, q.level - 1)} to unlock`
                              : undefined
                          }
                        >
                          <img
                            className="op-quest-glyph"
                            src={
                              locked
                                ? "/brand/outpost16-quest-level-lock.png"
                                : questTypeGlyphSrc(q)
                            }
                            alt=""
                            width={36}
                            height={36}
                          />
                          <div className="op-item-copy">
                            <h3>{q.title}</h3>
                            <small>
                              L{q.level} · {locked ? "locked" : q.status}
                            </small>
                          </div>
                          <button
                            type="button"
                            className="op-primary"
                            disabled={locked || busyQuestId === q.id}
                            onClick={() => void acceptQuest(q.id)}
                          >
                            Assign to me
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="op-section-title" id="op-home-goals">
                  <h2>Actionable goals</h2>
                  <span className="op-tag">Count + evidence</span>
                </div>
                <div className="op-list">
                  {goals.length === 0 ? (
                    <div className="op-list-item">
                      <img
                        className="op-empty-icon"
                        src="/brand/outpost16-no-goals-signal-flag.png"
                        alt=""
                        width={44}
                        height={44}
                      />
                      <div className="op-item-copy">
                        <h3>No goals yet</h3>
                        <small>Define an L1 goal on Quests.</small>
                      </div>
                    </div>
                  ) : (
                    goals.map((g) => (
                      <div
                        key={g.id}
                        id={`op-goal-${g.id}`}
                        className="op-list-item"
                      >
                        <div className="op-item-copy">
                          <h3>{g.title}</h3>
                          <small>{g.status}</small>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </section>
            ) : null}

            {token && active === "monitoring" && monitoringTab === "growth" ? (
              <section className="op-pane">
                {!integrationsLoading && !hasLitConnections ? (
                  <div className="op-conn-jump">
                    <img
                      src="/brand/outpost16-empty-inbox-binoculars.png"
                      alt=""
                      width={88}
                      height={88}
                    />
                    <h2>No connections lit yet</h2>
                    <p>
                      Growth stays empty until a lookout source is connected.
                      Add Buffer, Analytics, or Search Console to start.
                    </p>
                    <button
                      type="button"
                      className="op-primary"
                      onClick={() => goToConnections(true)}
                    >
                      <Plug size={16} aria-hidden />
                      Go to Connections
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="op-growth-stage">
                      <div className="op-growth-embers" aria-hidden />
                      <div className="op-growth-stage-head">
                        <div>
                          <h2>Growth</h2>
                          <small>
                            {selectedProduct?.name ?? "All products"}
                          </small>
                        </div>
                        <div className="op-period" role="group" aria-label="Time window">
                          {WINDOWS.map((w) => (
                            <button
                              key={w.id}
                              type="button"
                              aria-pressed={metricWindow === w.id}
                              onClick={() => setMetricWindow(w.id)}
                            >
                              {w.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <GrowthTicker items={buildGrowthTicker()} />
                      <div
                        className="op-totals op-growth-totals op-change-hero"
                        aria-label="What changed"
                      >
                        {(() => {
                          const cmp = circle?.comparison;
                          const signups =
                            circle?.totals.newSignups ??
                            activationWindowCounts()?.signups ??
                            null;
                          const firstValue =
                            circle?.totals.firstValue ??
                            activationWindowCounts()?.firstValue ??
                            null;
                          const deltaSignups =
                            cmp?.deltaNewSignups ??
                            (activationWindowCounts()?.priorSignups != null &&
                            signups != null
                              ? signups -
                                (activationWindowCounts()?.priorSignups ?? 0)
                              : null);
                          const pctSignups =
                            cmp?.pctNewSignups ??
                            pctFromPrior(
                              signups,
                              activationWindowCounts()?.priorSignups,
                            );
                          const deltaFirst =
                            cmp?.deltaFirstValue ??
                            (activationWindowCounts()?.priorFirst != null &&
                            firstValue != null
                              ? firstValue -
                                (activationWindowCounts()?.priorFirst ?? 0)
                              : null);
                          const pctFirst =
                            cmp?.pctFirstValue ??
                            pctFromPrior(
                              firstValue,
                              activationWindowCounts()?.priorFirst,
                            );
                          const vsLabel =
                            cmp?.label ??
                            activationWindowCounts()?.comparisonLabel;
                          const fresh = syncFreshness(
                            circle?.asOf ?? posthogConnection?.lastSyncedAt,
                            circle?.status === "stale" ||
                              posthogConnection?.status === "stale",
                          );
                          return (
                            <>
                              <div className="op-total-card op-change-card">
                                <small>Signups</small>
                                <strong>{fmtNull(signups)}</strong>
                                <span
                                  className={`op-change-line is-${
                                    deltaSignups == null
                                      ? "flat"
                                      : deltaSignups > 0
                                        ? "up"
                                        : deltaSignups < 0
                                          ? "down"
                                          : "flat"
                                  }`}
                                >
                                  {formatSignedDelta(deltaSignups) ??
                                    (metricWindow === "day"
                                      ? "current only"
                                      : "no prior yet")}
                                  {formatPctDelta(pctSignups)
                                    ? ` (${formatPctDelta(pctSignups)})`
                                    : ""}
                                  {vsLabel ? ` ${vsLabel}` : ""}
                                </span>
                              </div>
                              <div className="op-total-card op-change-card">
                                <small>First value</small>
                                <strong>{fmtNull(firstValue)}</strong>
                                <span
                                  className={`op-change-line is-${
                                    deltaFirst == null
                                      ? "flat"
                                      : deltaFirst > 0
                                        ? "up"
                                        : deltaFirst < 0
                                          ? "down"
                                          : "flat"
                                  }`}
                                >
                                  {formatSignedDelta(deltaFirst) ??
                                    (metricWindow === "day"
                                      ? "current only"
                                      : "no prior yet")}
                                  {formatPctDelta(pctFirst)
                                    ? ` (${formatPctDelta(pctFirst)})`
                                    : ""}
                                  {vsLabel ? ` ${vsLabel}` : ""}
                                </span>
                              </div>
                              <div className="op-total-card op-change-card is-meta">
                                <small>Freshness</small>
                                <strong className={`is-${fresh}`}>
                                  {fresh === "live"
                                    ? "Live"
                                    : fresh === "stale"
                                      ? "Stale"
                                      : "Quiet"}
                                </strong>
                                <span className="op-change-line is-flat">
                                  {fmtSyncedRelative(
                                    circle?.asOf ??
                                      posthogConnection?.lastSyncedAt,
                                    circle?.status === "stale" ||
                                      posthogConnection?.status === "stale",
                                  )}
                                </span>
                              </div>
                              <div className="op-total-card op-change-card is-billing">
                                <small>Billing</small>
                                <strong className="op-billing-muted">—</strong>
                                <span className="op-change-line is-flat">
                                  Not connected
                                </span>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                    {circleLoading ? (
                      <p className="op-notice">Loading customer circle…</p>
                    ) : null}
                    {circleError ? (
                      <p className="op-notice err">{circleError}</p>
                    ) : null}
                    <div className="op-grid op-growth-grid op-growth-gallery">
                      {posthogGrowth ? (
                        <GrowthSignalChapter
                          title="Activation"
                          eyebrow={`PostHog · ${
                            metricWindow === "day"
                              ? "last 1 day"
                              : metricWindow === "week"
                                ? "last 7 days"
                                : "last 30 days"
                          } · ${posthogGrowth.scopeLabel}${fmtSynced(posthogConnection?.lastSyncedAt, posthogConnection?.status === "stale")}`}
                          tag="Did activation move?"
                          freshness={syncFreshness(
                            posthogConnection?.lastSyncedAt,
                            posthogConnection?.status === "stale",
                          )}
                          toolLinks={[
                            {
                              label: "Open PostHog",
                              href: posthogToolHref(),
                            },
                          ]}
                          hero={{
                            value: fmtNull(
                              activationWindowCounts()?.signups ??
                                posthogGrowth.signupCount7d,
                            ),
                            label: `Signups ${
                              activationWindowCounts()?.windowLabel ?? "7d"
                            }`,
                            delta:
                              activationWindowCounts()?.priorSignups != null &&
                              activationWindowCounts()?.signups != null
                                ? (activationWindowCounts()?.signups ?? 0) -
                                  (activationWindowCounts()?.priorSignups ?? 0)
                                : null,
                            pctLabel: formatPctDelta(
                              pctFromPrior(
                                activationWindowCounts()?.signups,
                                activationWindowCounts()?.priorSignups,
                              ),
                            ),
                            comparisonLabel:
                              activationWindowCounts()?.comparisonLabel,
                          }}
                          series={posthogGrowth.signupSeries}
                          supports={[
                            {
                              label: `First value ${
                                activationWindowCounts()?.windowLabel ?? "7d"
                              }`,
                              value: fmtNull(
                                activationWindowCounts()?.firstValue ??
                                  posthogGrowth.firstValueCount7d,
                              ),
                              delta:
                                activationWindowCounts()?.priorFirst != null &&
                                activationWindowCounts()?.firstValue != null
                                  ? (activationWindowCounts()?.firstValue ??
                                      0) -
                                    (activationWindowCounts()?.priorFirst ?? 0)
                                  : null,
                            },
                            {
                              label:
                                posthogGrowth.exceptionCount7d != null
                                  ? "Exceptions 7d"
                                  : "Open errors",
                              value: fmtNull(
                                posthogGrowth.exceptionCount7d ??
                                  posthogGrowth.openErrorIssues,
                              ),
                              polarity: "lower-better",
                            },
                          ]}
                          unmatched={posthogGrowth.unmatched}
                          unmatchedMessage={
                            <>
                              No PostHog project mapped to this product yet.{" "}
                              <button
                                type="button"
                                className="op-linkish"
                                onClick={() => goToConnections(false)}
                              >
                                Map in Connections
                              </button>
                            </>
                          }
                          note={
                            <>
                              {posthogGrowth.unmappedCount > 0
                                ? `${posthogGrowth.unmappedCount} project${posthogGrowth.unmappedCount === 1 ? "" : "s"} still need signup / first-value event names. `
                                : ""}
                              In-app activation — not marketing traffic.
                              {metricWindow === "day"
                                ? " Day window is current-only (no prior comparison yet)."
                                : ""}
                            </>
                          }
                          actions={
                            <>
                              <button
                                type="button"
                                className="op-linkish"
                                onClick={() => setMonitoringTab("sessions")}
                              >
                                View sessions
                              </button>
                              <button
                                type="button"
                                className="op-linkish"
                                onClick={() => goToConnections(false)}
                              >
                                Sync in Connections
                              </button>
                            </>
                          }
                        />
                      ) : null}

                      {googleGrowth ? (
                        <GrowthSignalChapter
                          title="Site traffic"
                          eyebrow={`Google Analytics · last ${googleGrowth.windowLabel} · ${googleGrowth.scopeLabel}${fmtSynced(gaConnection?.lastSyncedAt)}`}
                          tag="Is traffic moving?"
                          freshness={syncFreshness(
                            gaConnection?.lastSyncedAt,
                            gaConnection?.status === "stale",
                          )}
                          windows={WINDOWS.map((w) => ({
                            id: w.id,
                            label: w.label,
                          }))}
                          windowId={metricWindow}
                          onWindowChange={(id) =>
                            setMetricWindow(id as MetricWindow)
                          }
                          toolLinks={[
                            {
                              label: "Open Analytics",
                              href: "https://analytics.google.com/",
                            },
                          ]}
                          hero={{
                            value: fmtNull(
                              googleGrowth.activeUsers ??
                                googleGrowth.sessions7d,
                            ),
                            label:
                              googleGrowth.activeUsers != null
                                ? "Active users"
                                : `GA sessions ${googleGrowth.windowLabel}`,
                            delta:
                              googleGrowth.activeUsers != null &&
                              googleGrowth.activeUsersPrior != null
                                ? googleGrowth.activeUsers -
                                  googleGrowth.activeUsersPrior
                                : googleGrowth.sessions7d != null &&
                                    googleGrowth.sessions7dPrior != null
                                  ? googleGrowth.sessions7d -
                                    googleGrowth.sessions7dPrior
                                  : null,
                            pctLabel: formatPctDelta(
                              pctFromPrior(
                                googleGrowth.activeUsers ??
                                  googleGrowth.sessions7d,
                                googleGrowth.activeUsersPrior ??
                                  googleGrowth.sessions7dPrior,
                              ),
                            ),
                            comparisonLabel:
                              (googleGrowth.activeUsersPrior ??
                                googleGrowth.sessions7dPrior) != null
                                ? `vs prior ${googleGrowth.windowLabel}`
                                : "no prior yet",
                          }}
                          compactTiles={[
                            {
                              id: "active",
                              label: "Active users",
                              value: fmtNull(googleGrowth.activeUsers),
                              delta:
                                googleGrowth.activeUsers != null &&
                                googleGrowth.activeUsersPrior != null
                                  ? googleGrowth.activeUsers -
                                    googleGrowth.activeUsersPrior
                                  : null,
                              pctLabel: formatPctDelta(
                                pctFromPrior(
                                  googleGrowth.activeUsers,
                                  googleGrowth.activeUsersPrior,
                                ),
                              ),
                              tip: `Active users in the last ${googleGrowth.windowLabel}.`,
                            },
                            {
                              id: "new",
                              label: "New users",
                              value: fmtNull(googleGrowth.newUsers),
                              delta:
                                googleGrowth.newUsers != null &&
                                googleGrowth.newUsersPrior != null
                                  ? googleGrowth.newUsers -
                                    googleGrowth.newUsersPrior
                                  : null,
                              pctLabel: formatPctDelta(
                                pctFromPrior(
                                  googleGrowth.newUsers,
                                  googleGrowth.newUsersPrior,
                                ),
                              ),
                              tip: `First-time users in the last ${googleGrowth.windowLabel}.`,
                            },
                            {
                              id: "eng",
                              label: "Avg engagement",
                              value:
                                googleGrowth.avgEngagementSec != null
                                  ? `${googleGrowth.avgEngagementSec}s`
                                  : "—",
                              delta:
                                googleGrowth.avgEngagementSec != null &&
                                googleGrowth.avgEngagementSecPrior != null
                                  ? googleGrowth.avgEngagementSec -
                                    googleGrowth.avgEngagementSecPrior
                                  : null,
                              pctLabel: formatPctDelta(
                                pctFromPrior(
                                  googleGrowth.avgEngagementSec,
                                  googleGrowth.avgEngagementSecPrior,
                                ),
                              ),
                              tip: "Average engagement time per active user.",
                            },
                            {
                              id: "events",
                              label: "Event count",
                              value: fmtNull(googleGrowth.eventCount),
                              delta:
                                googleGrowth.eventCount != null &&
                                googleGrowth.eventCountPrior != null
                                  ? googleGrowth.eventCount -
                                    googleGrowth.eventCountPrior
                                  : null,
                              pctLabel: formatPctDelta(
                                pctFromPrior(
                                  googleGrowth.eventCount,
                                  googleGrowth.eventCountPrior,
                                ),
                              ),
                              tip: `Total GA4 events in the last ${googleGrowth.windowLabel}.`,
                            },
                          ]}
                          supports={[]}
                          breakdowns={[
                            {
                              title: "Active users by country",
                              dimensionLabel: "Country",
                              metricLabel: "Active users",
                              rows: googleGrowth.byCountry.map((r) => ({
                                name: r.name,
                                value: fmtNull(r.value),
                                delta:
                                  r.valuePrior != null
                                    ? r.value - r.valuePrior
                                    : null,
                                pctLabel: formatPctDelta(
                                  pctFromPrior(r.value, r.valuePrior),
                                ),
                              })),
                            },
                            {
                              title: "Views by page title",
                              dimensionLabel: "Page title",
                              metricLabel: "Active users",
                              rows: googleGrowth.byPage.map((r) => ({
                                name: r.name,
                                value: fmtNull(r.value),
                                delta:
                                  r.valuePrior != null
                                    ? r.value - r.valuePrior
                                    : null,
                                pctLabel: formatPctDelta(
                                  pctFromPrior(r.value, r.valuePrior),
                                ),
                              })),
                            },
                            {
                              title: "Sessions by channel",
                              dimensionLabel: "Channel group",
                              metricLabel: "Active users",
                              rows: googleGrowth.byChannel.map((r) => ({
                                name: r.name,
                                value: fmtNull(r.value),
                                delta:
                                  r.valuePrior != null
                                    ? r.value - r.valuePrior
                                    : null,
                                pctLabel: formatPctDelta(
                                  pctFromPrior(r.value, r.valuePrior),
                                ),
                              })),
                            },
                          ]}
                          unmatched={googleGrowth.unmatched}
                          unmatchedMessage={
                            <>
                              No GA property mapped to{" "}
                              <strong>{selectedProduct?.name}</strong> yet.{" "}
                              <button
                                type="button"
                                className="op-linkish"
                                onClick={() => goToConnections(false)}
                              >
                                Re-save mappings in Connections
                              </button>
                              , then Sync — multi-product maps are preserved on
                              background sync now.
                            </>
                          }
                          note={
                            productFilter === "all"
                              ? "All mapped GA properties rolled up."
                              : `Scoped to ${googleGrowth.scopeLabel}.`
                          }
                          actions={
                            <button
                              type="button"
                              className="op-linkish"
                              onClick={() => goToConnections(false)}
                            >
                              Manage mappings
                            </button>
                          }
                        />
                      ) : null}

                      {gscGrowth ? (
                        <GrowthSignalChapter
                          title="Google Search"
                          eyebrow={`Search Console · last ${gscGrowth.windowLabel} · ${gscGrowth.scopeLabel}${fmtSynced(gscConnection?.lastSyncedAt)}`}
                          tag="Is search moving?"
                          freshness={syncFreshness(
                            gscConnection?.lastSyncedAt,
                            gscConnection?.status === "stale",
                          )}
                          windows={WINDOWS.map((w) => ({
                            id: w.id,
                            label: w.label,
                          }))}
                          windowId={metricWindow}
                          onWindowChange={(id) =>
                            setMetricWindow(id as MetricWindow)
                          }
                          toolLinks={[
                            {
                              label: "Open Search Console",
                              href:
                                gscGrowth.indexStatus?.indexingReportUrl ??
                                "https://search.google.com/search-console",
                            },
                          ]}
                          hero={{
                            value: fmtNull(gscGrowth.clicks),
                            label: `Clicks · ${gscGrowth.windowLabel}`,
                            delta:
                              gscGrowth.clicks != null &&
                              gscGrowth.clicksPrior != null
                                ? gscGrowth.clicks - gscGrowth.clicksPrior
                                : null,
                            pctLabel: formatPctDelta(
                              pctFromPrior(
                                gscGrowth.clicks,
                                gscGrowth.clicksPrior,
                              ),
                            ),
                            comparisonLabel:
                              gscGrowth.clicksPrior != null
                                ? `vs prior ${gscGrowth.windowLabel}`
                                : "no prior yet",
                          }}
                          compactTiles={[
                            {
                              id: "clicks",
                              label: "Clicks",
                              value: fmtNull(gscGrowth.clicks),
                              delta:
                                gscGrowth.clicks != null &&
                                gscGrowth.clicksPrior != null
                                  ? gscGrowth.clicks - gscGrowth.clicksPrior
                                  : null,
                              pctLabel: formatPctDelta(
                                pctFromPrior(
                                  gscGrowth.clicks,
                                  gscGrowth.clicksPrior,
                                ),
                              ),
                              tip: `Search clicks in the last ${gscGrowth.windowLabel}.`,
                            },
                            {
                              id: "views",
                              label: "Views",
                              value: fmtNull(gscGrowth.impressions),
                              delta:
                                gscGrowth.impressions != null &&
                                gscGrowth.impressionsPrior != null
                                  ? gscGrowth.impressions -
                                    gscGrowth.impressionsPrior
                                  : null,
                              pctLabel: formatPctDelta(
                                pctFromPrior(
                                  gscGrowth.impressions,
                                  gscGrowth.impressionsPrior,
                                ),
                              ),
                              tip: `Search impressions (views) in the last ${gscGrowth.windowLabel}.`,
                            },
                            {
                              id: "position",
                              label: "Avg position",
                              value:
                                gscGrowth.position != null
                                  ? String(gscGrowth.position)
                                  : "—",
                              delta:
                                gscGrowth.position != null &&
                                gscGrowth.positionPrior != null
                                  ? gscGrowth.position -
                                    gscGrowth.positionPrior
                                  : null,
                              pctLabel: formatPctDelta(
                                pctFromPrior(
                                  gscGrowth.position,
                                  gscGrowth.positionPrior,
                                ),
                              ),
                              polarity: "lower-better",
                              tip: `Average search position (lower is better) over the last ${gscGrowth.windowLabel}.`,
                            },
                            {
                              id: "indexed",
                              label: "Indexed",
                              value:
                                gscGrowth.indexStatus?.sitemapIndexed != null
                                  ? fmtNull(
                                      gscGrowth.indexStatus.sitemapIndexed,
                                    )
                                  : "—",
                              tip:
                                gscGrowth.indexStatus?.sitemapIndexed != null
                                  ? "Pages marked indexed in submitted sitemaps (approximate)."
                                  : "Open Search Console for live Page indexing counts.",
                              href:
                                gscGrowth.indexStatus?.indexingReportUrl ??
                                undefined,
                            },
                          ]}
                          holdover={(() => {
                            const idx = gscGrowth.indexStatus;
                            const lastCrawl = idx?.lastCrawlTime
                              ? new Date(idx.lastCrawlTime).toLocaleString(
                                  undefined,
                                  {
                                    dateStyle: "medium",
                                    timeStyle: "short",
                                  },
                                )
                              : null;
                            const notIndexed =
                              idx?.sitemapSubmitted != null &&
                              idx?.sitemapIndexed != null
                                ? Math.max(
                                    0,
                                    idx.sitemapSubmitted - idx.sitemapIndexed,
                                  )
                                : null;
                            const reasonsHref =
                              idx?.notIndexedReportUrl ??
                              idx?.indexingReportUrl ??
                              null;
                            if (!lastCrawl && notIndexed == null && !idx?.coverageState) {
                              return null;
                            }
                            return {
                              iconTip: lastCrawl
                                ? `Last indexed ${lastCrawl}`
                                : idx?.coverageState
                                  ? `Homepage · ${idx.coverageState}`
                                  : "Last indexed date unavailable",
                              link:
                                notIndexed != null && reasonsHref
                                  ? {
                                      href: reasonsHref,
                                      label: `${fmtNull(notIndexed)} not indexed`,
                                    }
                                  : notIndexed != null
                                    ? {
                                        href:
                                          idx?.indexingReportUrl ??
                                          "https://search.google.com/search-console",
                                        label: `${fmtNull(notIndexed)} not indexed`,
                                      }
                                    : null,
                            };
                          })()}
                          supports={[]}
                          unmatched={gscGrowth.unmatched}
                          unmatchedMessage={
                            <>
                              No Search Console site mapped to{" "}
                              <strong>{selectedProduct?.name}</strong> yet.{" "}
                              <button
                                type="button"
                                className="op-linkish"
                                onClick={() => goToConnections(false)}
                              >
                                Map sites in Connections
                              </button>
                              .
                            </>
                          }
                          note={
                            productFilter === "all"
                              ? "All mapped Search Console sites rolled up."
                              : `Scoped to ${gscGrowth.scopeLabel}.`
                          }
                          actions={
                            <button
                              type="button"
                              className="op-linkish"
                              onClick={() => goToConnections(false)}
                            >
                              Manage mappings
                            </button>
                          }
                        />
                      ) : null}

                      {ahrefsGrowth ? (
                        <GrowthSignalChapter
                          title="Authority"
                          eyebrow={`Ahrefs · ${ahrefsGrowth.scopeLabel}${fmtSynced(ahrefsConnection?.lastSyncedAt)}`}
                          tag="Supporting SEO"
                          freshness={syncFreshness(
                            ahrefsConnection?.lastSyncedAt,
                            ahrefsConnection?.status === "stale",
                          )}
                          toolLinks={[
                            {
                              label: "Open Ahrefs",
                              href: "https://app.ahrefs.com/",
                            },
                          ]}
                          hero={{
                            value: fmtNull(ahrefsGrowth.domainRating),
                            label: "Domain Rating",
                            delta:
                              ahrefsGrowth.drDeltaSinceBaseline ??
                              ahrefsGrowth.drDelta30d,
                          }}
                          series={ahrefsGrowth.series}
                          supports={[
                            {
                              label:
                                ahrefsGrowth.drDeltaSinceBaseline != null
                                  ? "Δ since baseline"
                                  : "DR Δ 30d",
                              value:
                                fmtDelta(
                                  ahrefsGrowth.drDeltaSinceBaseline ??
                                    ahrefsGrowth.drDelta30d,
                                ) ?? "—",
                              delta:
                                ahrefsGrowth.drDeltaSinceBaseline ??
                                ahrefsGrowth.drDelta30d,
                            },
                            {
                              label:
                                ahrefsGrowth.apiMode === "public"
                                  ? "Domains"
                                  : "Referring domains",
                              value:
                                ahrefsGrowth.apiMode === "public"
                                  ? String(ahrefsGrowth.targetCount)
                                  : fmtNull(ahrefsGrowth.liveRefdomains),
                            },
                          ]}
                          unmatched={ahrefsGrowth.unmatched}
                          unmatchedMessage={
                            <>
                              No Ahrefs domain mapped to{" "}
                              <strong>{selectedProduct?.name}</strong> yet.
                            </>
                          }
                          note={
                            <>
                              {ahrefsGrowth.apiMode === "public"
                                ? ahrefsGrowth.drDeltaSinceBaseline == null
                                  ? "Public key — DR only; sparkline grows after more syncs. "
                                  : "Public key — DR + change since first Outpost baseline. "
                                : "Supporting SEO signal — not a substitute for signups. "}
                              {ahrefsGrowth.attribution ? (
                                <a
                                  href="https://ahrefs.com/"
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {ahrefsGrowth.attribution}
                                </a>
                              ) : null}
                            </>
                          }
                          actions={
                            <button
                              type="button"
                              className="op-linkish"
                              onClick={() => goToConnections(false)}
                            >
                              Sync in Connections
                            </button>
                          }
                        />
                      ) : null}

                      {bufferGrowth ? (
                        (() => {
                          const queueBand = bufferQueueBand(
                            bufferGrowth.scheduledCount,
                            bufferGrowth.channelCount,
                          );
                          const bandLabel = bufferQueueBandLabel(
                            queueBand,
                            bufferGrowth.channelCount,
                          );
                          const absDelta = (
                            cur: number | null,
                            prior: number | null,
                          ) =>
                            cur != null && prior != null ? cur - prior : null;
                          const tile = (
                            id: string,
                            label: string,
                            value: string,
                            cur: number | null,
                            prior: number | null,
                            tip: string,
                            band?: "red" | "yellow" | "green" | null,
                          ): GrowthMetricTile => {
                            const delta = absDelta(cur, prior);
                            const pct = pctFromPrior(cur, prior);
                            return {
                              id,
                              label,
                              value,
                              delta,
                              pctLabel: formatPctDelta(pct),
                              tip,
                              band: band ?? null,
                            };
                          };
                          const compactTiles: GrowthMetricTile[] = [
                            tile(
                              "queued",
                              "Queued",
                              `${fmtNull(bufferGrowth.scheduledCount)}${bufferGrowth.scheduledTruncated ? "+" : ""}`,
                              bufferGrowth.scheduledCount,
                              null,
                              bandLabel
                                ? `Queue refill band: ${bandLabel}. Thresholds scale with channel count.`
                                : "Posts still waiting in Buffer queues.",
                              queueBand,
                            ),
                            tile(
                              "posts",
                              "Posts",
                              `${fmtNull(bufferGrowth.published)}${bufferGrowth.publishedTruncated ? "+" : ""}`,
                              bufferGrowth.published,
                              bufferGrowth.publishedPrior,
                              `Published posts in this ${bufferGrowth.windowLabel} window.`,
                            ),
                            tile(
                              "impressions",
                              "Impressions",
                              fmtNull(bufferGrowth.impressions),
                              bufferGrowth.impressions,
                              bufferGrowth.impressionsPrior,
                              "How many times Buffer posts were shown.",
                            ),
                            tile(
                              "eng",
                              "Eng rate",
                              bufferGrowth.engRatePct != null
                                ? `${bufferGrowth.engRatePct}%`
                                : "—",
                              bufferGrowth.engRatePct,
                              bufferGrowth.engRatePctPrior,
                              "Approx. (reactions + comments + reposts + clicks) ÷ impressions. Not Buffer’s billed eng. rate.",
                            ),
                          ];
                          const expandedTiles: GrowthMetricTile[] = [
                            tile(
                              "reactions",
                              "Reactions",
                              fmtNull(bufferGrowth.reactions),
                              bufferGrowth.reactions,
                              bufferGrowth.reactionsPrior,
                              "Likes / reactions attributed to Buffer posts.",
                            ),
                            tile(
                              "comments",
                              "Comments",
                              fmtNull(bufferGrowth.comments),
                              bufferGrowth.comments,
                              bufferGrowth.commentsPrior,
                              "Comments on Buffer-published posts.",
                            ),
                            tile(
                              "reposts",
                              "Reposts",
                              fmtNull(bufferGrowth.reposts),
                              bufferGrowth.reposts,
                              bufferGrowth.repostsPrior,
                              "Shares / reposts of Buffer-published posts.",
                            ),
                            tile(
                              "clicks",
                              "Clicks",
                              fmtNull(bufferGrowth.clicks),
                              bufferGrowth.clicks,
                              bufferGrowth.clicksPrior,
                              "Link clicks from Buffer posts.",
                            ),
                          ];
                          const lastSyncLabel = bufferConnection?.lastSyncedAt
                            ? `Last sync ${new Date(
                                bufferConnection.lastSyncedAt.includes("T")
                                  ? bufferConnection.lastSyncedAt
                                  : `${bufferConnection.lastSyncedAt}Z`,
                              ).toLocaleString(undefined, {
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })}`
                            : "Not synced yet";
                          return (
                            <GrowthSignalChapter
                              title="Social publish"
                              eyebrow={bufferGrowth.scopeLabel}
                              tag="Still stocked?"
                              freshness={syncFreshness(
                                bufferConnection?.lastSyncedAt,
                                bufferConnection?.status === "stale",
                              )}
                              windows={[
                                { id: "day", label: "Day" },
                                { id: "week", label: "Week" },
                                { id: "month", label: "Month" },
                              ]}
                              windowId={bufferMetricWindow}
                              toolLinks={[
                                {
                                  label: "Open Buffer",
                                  href: "https://publish.buffer.com/",
                                },
                              ]}
                              hero={{
                                value: `${fmtNull(bufferGrowth.scheduledCount)}${bufferGrowth.scheduledTruncated ? "+" : ""}`,
                                label: "Queued",
                                band: queueBand,
                                comparisonLabel:
                                  bandLabel ?? "posts waiting to go out",
                              }}
                              supports={[]}
                              compactTiles={compactTiles}
                              expandedTiles={expandedTiles}
                              footerNote={lastSyncLabel}
                              unmatched={bufferGrowth.unmatched}
                              unmatchedMessage={
                                <>
                                  No Buffer channel matched{" "}
                                  <strong>{selectedProduct?.name}</strong> yet.
                                  Channels:{" "}
                                  {(bufferStatsRaw?.channels ?? [])
                                    .map((c) => c.name)
                                    .join(", ") || "none"}
                                  .
                                </>
                              }
                              actions={
                                <button
                                  type="button"
                                  className="op-linkish"
                                  onClick={() => goToConnections(false)}
                                >
                                  Sync in Connections
                                </button>
                              }
                            />
                          );
                        })()
                      ) : null}

                      {instantlyGrowth ? (
                        <GrowthSignalChapter
                          title="Outbound"
                          eyebrow={`Instantly · ${instantlyGrowth.mode === "warmup" ? "warmup" : "last 7 days"} · ${instantlyGrowth.scopeLabel}${fmtSynced(instantlyConnection?.lastSyncedAt, instantlyConnection?.status === "stale")}`}
                          tag={
                            instantlyGrowth.mode === "warmup"
                              ? "Inbox ready?"
                              : "Any replies?"
                          }
                          freshness={syncFreshness(
                            instantlyConnection?.lastSyncedAt,
                            instantlyConnection?.status === "stale",
                          )}
                          toolLinks={[
                            {
                              label: "Open Instantly",
                              href: "https://app.instantly.ai/",
                            },
                          ]}
                          hero={
                            instantlyGrowth.mode === "warmup"
                              ? {
                                  value: fmtNull(
                                    instantlyGrowth.avgHealthScore,
                                  ),
                                  label: "Avg health",
                                }
                              : {
                                  value: fmtNull(instantlyGrowth.replied7d),
                                  label: "Replied 7d",
                                }
                          }
                          series={
                            instantlyGrowth.mode === "warmup" &&
                            instantlyGrowth.series.length >= 2
                              ? instantlyGrowth.series
                              : undefined
                          }
                          supports={
                            instantlyGrowth.mode === "warmup"
                              ? [
                                  {
                                    label: "Accounts",
                                    value: fmtNull(
                                      instantlyGrowth.accountCount || null,
                                    ),
                                  },
                                  {
                                    label: "Spam landings",
                                    value: fmtNull(
                                      instantlyGrowth.spamLandings,
                                    ),
                                    polarity: "lower-better" as const,
                                  },
                                ]
                              : [
                                  {
                                    label: "Sent 7d",
                                    value: fmtNull(instantlyGrowth.sent7d),
                                  },
                                  {
                                    label: "Opened",
                                    value: fmtNull(instantlyGrowth.opened7d),
                                  },
                                ]
                          }
                          note={
                            instantlyGrowth.mode === "warmup"
                              ? instantlyGrowth.warmupError
                                ? `Warmup analytics unavailable: ${instantlyGrowth.warmupError}`
                                : instantlyGrowth.inboxRate != null
                                  ? `Inbox land rate ${fmtRatePct(instantlyGrowth.inboxRate)}. Campaign sends still quiet — health first.`
                                  : "Warming inboxes before campaign volume. Health is the lookout."
                              : instantlyGrowth.clicked7d != null
                                ? `Clicked ${fmtNull(instantlyGrowth.clicked7d)} in window. Supporting signal only.`
                                : "Campaign replies are the hero once sends are live."
                          }
                          actions={
                            <button
                              type="button"
                              className="op-linkish"
                              onClick={() => goToConnections(false)}
                            >
                              Sync in Connections
                            </button>
                          }
                        />
                      ) : null}
                    </div>
                  </>
                )}
              </section>
            ) : null}

            {token &&
            active === "monitoring" &&
            monitoringTab === "connections" ? (
              <section className="op-pane">
                <ConnectionsPanel
                  products={products}
                  integrations={integrations}
                  loading={integrationsLoading}
                  error={integrationsError}
                  notice={integrationNotice}
                  actionError={integrationActionError}
                  secretsKeyConfigured={secretsKeyConfigured}
                  ahrefsPlatformKeyConfigured={ahrefsPlatformKeyConfigured}
                  busy={integrationBusy}
                  pickerOpen={connPickerOpen}
                  draftProvider={draftProvider}
                  tuneProvider={tuneProvider}
                  bufferApiKey={bufferApiKey}
                  instantlyApiKey={instantlyApiKey}
                  ahrefsApiKey={ahrefsApiKey}
                  googleClientId={googleClientId}
                  googleClientSecret={googleClientSecret}
                  onPickerOpen={setConnPickerOpen}
                  onDraftProvider={setDraftProvider}
                  onTuneProvider={setTuneProvider}
                  onBufferApiKey={setBufferApiKey}
                  onInstantlyApiKey={setInstantlyApiKey}
                  onAhrefsApiKey={setAhrefsApiKey}
                  onGoogleClientId={setGoogleClientId}
                  onGoogleClientSecret={setGoogleClientSecret}
                  onGoogleAuthorize={(payload) => {
                    void startGoogleOAuth(payload);
                  }}
                  onGoogleSaveMappings={(payload, surface) => {
                    void saveGoogleMappings(payload, surface);
                  }}
                  onAhrefsSave={(payload) => {
                    void saveAhrefs(payload);
                  }}
                  onPostHogSave={(payload) => {
                    void savePostHog(payload);
                  }}
                  onAction={(provider, action, body) => {
                    void runIntegrationAction(provider, action, body);
                  }}
                />
              </section>
            ) : null}

            {token &&
            active === "monitoring" &&
            monitoringTab === "sessions" ? (
              <section className="op-pane">
                <div className="op-panel">
                  <div className="op-panel-head">
                    <div>
                      <h2>Sessions</h2>
                      <small>
                        PostHog replays · {posthogGrowth?.scopeLabel ?? "All products"}
                        {posthogConnection?.lastSyncedAt
                          ? ` · ${fmtSyncedRelative(
                              posthogConnection.lastSyncedAt,
                              syncFreshness(posthogConnection.lastSyncedAt) ===
                                "stale",
                            )}`
                          : ""}
                      </small>
                    </div>
                    <button
                      type="button"
                      className="op-btn"
                      disabled={!!integrationBusy}
                      onClick={() => {
                        if (posthogConnection) {
                          void runIntegrationAction("posthog", "sync");
                        } else {
                          goToConnections(true);
                        }
                      }}
                    >
                      {posthogConnection ? "Sync" : "Connect"}
                    </button>
                  </div>
                  {!posthogGrowth ? (
                    <div className="op-list-item op-community-empty">
                      <div className="op-item-copy">
                        <h3>No PostHog sessions yet</h3>
                        <small>
                          Connect a per-product PostHog project in Connections,
                          then sync to pull recent recordings.
                        </small>
                      </div>
                    </div>
                  ) : posthogGrowth.sessions.length === 0 ? (
                    <p className="op-growth-buffer-note">
                      Connected — no recent recordings in the last sync
                      {posthogGrowth.unmatched
                        ? " for this product filter"
                        : ""}
                      .
                    </p>
                  ) : (
                    <div className="op-sessions-list">
                      {posthogGrowth.sessions.map((s) => (
                        <div key={s.id} className="op-sessions-row">
                          <div className="op-item-copy">
                            <strong>
                              {s.personDisplay || "Anonymous"}
                              {s.productLabel ? ` · ${s.productLabel}` : ""}
                            </strong>
                            <small>
                              {s.startTime
                                ? new Date(s.startTime).toLocaleString()
                                : "—"}
                              {s.durationSec != null
                                ? ` · ${s.durationSec}s`
                                : ""}
                              {s.startUrl ? ` · ${s.startUrl}` : ""}
                            </small>
                          </div>
                          <a
                            className="op-btn"
                            href={s.replayUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Open replay
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            ) : null}

            {token && active === "monitoring" && monitoringTab === "ops" ? (
              <section className="op-pane">
                <OpsPane
                  token={token}
                  productId={
                    productFilter !== "all" ? productFilter : metricProductId
                  }
                  productName={
                    products.find(
                      (p) =>
                        p.id ===
                        (productFilter !== "all"
                          ? productFilter
                          : metricProductId),
                    )?.name ?? null
                  }
                  products={products.map((p) => ({ id: p.id, name: p.name }))}
                  onPickProduct={(id) => {
                    setProductFilter(id);
                    setMetricProductId(id);
                  }}
                  instantlyConnected={integrations.some(
                    (i) =>
                      i.provider === "instantly" &&
                      (i.status === "connected" ||
                        i.status === "stale" ||
                        i.credentialsConfigured),
                  )}
                  bufferConnected={integrations.some(
                    (i) =>
                      i.provider === "buffer" &&
                      (i.status === "connected" ||
                        i.status === "stale" ||
                        i.credentialsConfigured),
                  )}
                  onManageConnection={() => goToConnections(false)}
                  onCreateQuest={({ jobKey, label, gap }) => {
                    const seedId = opsGapToSeedId(gap);
                    const seed = SEED_QUEST_CATALOG.find((s) => s.id === seedId);
                    if (!seed) {
                      setQuestNotice(
                        `Improve “${label}” (${jobKey}) · gap: ${gap}. Catalog template missing.`,
                      );
                      focusNewQuest();
                      return;
                    }
                    setQuestNotice(
                      `Ops → ${seed.id}: ${seed.title} · from “${label}” (${gap})`,
                    );
                    void assignSeedQuest(seed);
                    focusNewQuest();
                  }}
                />
              </section>
            ) : null}

            {token && active === "quests" ? (
              <QuestsPane
                quests={quests}
                lookoutLevel={lookoutLevel}
                lookoutXp={lookoutXp}
                questNotice={questNotice}
                questCatalogRef={questCatalogRef}
                busyQuestId={busyQuestId}
                proofDrafts={proofDrafts}
                setProofDrafts={setProofDrafts}
                onAssignSeed={(seed) => void assignSeedQuest(seed)}
                onSubmitProof={(id) => void submitProof(id)}
                onConfirm={(id, isTrue) => void confirmQuest(id, isTrue)}
                onLeave={(id) => void leaveQuest(id)}
                expandedActiveId={expandedActiveId}
                setExpandedActiveId={setExpandedActiveId}
                expandedCatalogId={expandedCatalogId}
                setExpandedCatalogId={setExpandedCatalogId}
                expandedDecade={expandedDecade}
                setExpandedDecade={setExpandedDecade}
                myActiveOpen={myActiveOpen}
                setMyActiveOpen={setMyActiveOpen}
                channelFilter={channelFilter}
                setChannelFilter={setChannelFilter}
                assigneeFilter={assigneeFilter}
                setAssigneeFilter={setAssigneeFilter}
                missionFilter={missionFilter}
                setMissionFilter={setMissionFilter}
              />
            ) : null}

            {token && active === "community" ? (
              <CommunityPane
                token={token}
                focusDest={communityFocus}
                focusNonce={communityFocusNonce}
                newStartupNonce={newStartupNonce}
                onDestChange={(dest) => {
                  if (
                    dest === "feed" ||
                    dest === "lookouts" ||
                    dest === "products" ||
                    dest === "profile" ||
                    dest === "new"
                  ) {
                    setCommunityFocus(dest);
                  } else if (dest === "share") {
                    setCommunityFocus("feed");
                  }
                }}
                onProductsChanged={() => {
                  void (async () => {
                    const p = await apiGet<{ products: Product[] }>(
                      "/v1/products",
                      token,
                    );
                    if (p.ok) setProducts(p.data.products);
                  })();
                }}
                lookoutLevel={lookoutLevel}
                lookoutXp={lookoutXp}
                products={products}
                logoPreviews={logoPreviews}
                logoBusyId={logoBusyId}
                onUploadLogo={(id, file) => void uploadProductLogo(id, file)}
              />
            ) : null}

            {token && active === "product_settings" ? (
              (() => {
                const product =
                  products.find((p) => p.id === settingsProductId) ??
                  products.find((p) => p.id === productFilter) ??
                  null;
                if (!product) {
                  return (
                    <section className="op-pane op-product-settings">
                      <div className="op-panel-head">
                        <div>
                          <h1>Product settings</h1>
                          <small>Pick a product space from the sidebar.</small>
                        </div>
                      </div>
                    </section>
                  );
                }
                return (
                  <section className="op-pane op-product-settings">
                    <div className="op-panel-head">
                      <div>
                        <h1>{product.name}</h1>
                        <small>Product space profile and assets.</small>
                      </div>
                      <button
                        type="button"
                        className="op-btn"
                        onClick={() => goNav("home")}
                      >
                        Done
                      </button>
                    </div>

                    <div className="op-panel op-product-settings-card">
                      <div className="op-panel-head">
                        <div>
                          <h2>Logo</h2>
                          <small>
                            Shown in the sidebar and Community directory.
                          </small>
                        </div>
                      </div>
                      <div className="op-profile-logo-row">
                        <span className="op-product-logo op-product-logo-lg">
                          {logoPreviews[product.id] ? (
                            <img src={logoPreviews[product.id]} alt="" />
                          ) : (
                            product.name.slice(0, 2).toUpperCase()
                          )}
                        </span>
                        <label className="op-profile-logo-upload">
                          <span>
                            {logoBusyId === product.id
                              ? "Uploading…"
                              : logoPreviews[product.id]
                                ? "Replace logo"
                                : "Upload logo"}
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            disabled={logoBusyId === product.id}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              e.target.value = "";
                              if (file) void uploadProductLogo(product.id, file);
                            }}
                          />
                        </label>
                      </div>
                    </div>

                    <div className="op-panel op-product-settings-card">
                      <div className="op-panel-head">
                        <div>
                          <h2>Identity</h2>
                          <small>Read-only for now — edit in Community.</small>
                        </div>
                      </div>
                      <dl className="op-product-settings-meta">
                        <div>
                          <dt>Name</dt>
                          <dd>{product.name}</dd>
                        </div>
                        <div>
                          <dt>ID</dt>
                          <dd>
                            <code>{product.id}</code>
                          </dd>
                        </div>
                      </dl>
                      <button
                        type="button"
                        className="op-btn"
                        onClick={() => {
                          selectProduct(product.id);
                          setActive("community");
                          setCommunityFocus("feed");
                          setCommunityFocusNonce((n) => n + 1);
                        }}
                      >
                        Open in Community
                      </button>
                    </div>
                  </section>
                );
              })()
            ) : null}

            {token && active === "agents" ? (
              <AgentsPane token={token} />
            ) : null}
            {token && active === "admin" && canAdmin ? (
              <AdminPane
                token={token}
                isPlatformAdmin={
                  me?.kind === "founder" ||
                  Boolean(me?.user?.isPlatformAdmin)
                }
                onOpenProduct={openProductSettings}
                onAddProduct={openAddProduct}
              />
            ) : null}
          </div>

          <div className="op-footer">
            <span>outpost16.com</span>
            <span>Slice H5 · no invented metrics</span>
          </div>
        </main>
      </div>
    </div>
  );
}
